/**
 * Phase 2L-A17 — Booking Timeline Page.
 *
 * Role-aware single-pane view at `/admin/bookings/:ref`. Renders the
 * payload returned by `/api/admin/bookings/{ref}/timeline` — admin gets
 * the full picture, lab tech gets a pruned version (server-side).
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import {
  Calendar, CreditCard, FlaskConical, Clipboard, Eye, Check,
  FileCheck2, Mail, MessageCircle, FilePlus2, Send, Shield,
  Lightbulb, Camera, StickyNote, AlertTriangle, CheckCircle2,
  Loader2, ArrowLeft, ArrowRight, Plus, X, ChevronDown, ChevronRight,
  Pencil, UserCog, Lock, Download, RefreshCw, Package, Truck, Inbox,
  Users,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PortalShell } from "@/components/PortalShell";
import PartnerNetworkPanels from "./PartnerNetworkPanels";
import StarshipitShippingPanel from "./StarshipitShippingPanel";
import { PWSStatusPanel } from "@/components/admin/PWSStatusPanel";
import api, { formatApiError } from "@/lib/api";
import { fetchAndOpenAuthed } from "@/lib/authedDownload";
import { TankCleanContext } from "@/components/TankCleanContext";
import { useAuth } from "@/lib/auth";
import { formatDateAU, formatDateTimeAU } from "@/lib/formatDateAU";
import { isBookingNoPaymentRequired } from "@/lib/bookingPaymentStatus";
import { parseAusAddress } from "@/lib/serviceArea";

const ICONS = {
  calendar: Calendar,
  "credit-card": CreditCard,
  flask: FlaskConical,
  clipboard: Clipboard,
  eye: Eye,
  check: Check,
  "file-check": FileCheck2,
  mail: Mail,
  "message-circle": MessageCircle,
  "file-plus": FilePlus2,
  send: Send,
  shield: Shield,
  lightbulb: Lightbulb,
  camera: Camera,
  "sticky-note": StickyNote,
  "user-pen": UserCog,
};

const KIND_TONES = {
  payment_paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  refunded: "bg-rose-50 text-rose-700 border-rose-200",
  email_failed: "bg-rose-50 text-rose-700 border-rose-200",
  report_published: "bg-emerald-50 text-emerald-700 border-emerald-200",
  admin_action: "bg-amber-50 text-amber-700 border-amber-200",
  internal_note: "bg-cyan-50 text-cyan-700 border-cyan-200",
  followup_received: "bg-violet-50 text-violet-700 border-violet-200",
  proposal_created: "bg-indigo-50 text-indigo-700 border-indigo-200",
  customer_reply_received: "bg-violet-50 text-violet-700 border-violet-200",
  customer_details_updated: "bg-amber-50 text-amber-700 border-amber-200",
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "lab", label: "Lab", kinds: ["sample_received","results_entered","submitted_for_review","report_approved","report_published","internal_note","photo_uploaded","recommendation_generated"] },
  { key: "payment", label: "Payment", kinds: ["payment_paid","payment_session_created","refunded"], adminOnly: true },
  { key: "email", label: "Email", kinds: ["email_sent","email_failed"] },
  { key: "admin", label: "Admin", kinds: ["admin_action","archived","restored","customer_details_updated"], adminOnly: true },
  { key: "proposal", label: "Proposal", kinds: ["proposal_created","proposal_sent","followup_received"], adminOnly: true },
];

export default function BookingDetailPage() {
  const { ref } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    document.title = `Booking ${ref} · Aware`;
    let alive = true;
    setLoading(true);
    setError(null);
    api.get(`/admin/bookings/${ref}/timeline`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => { if (alive) setError(e?.response?.status === 404 ? `Booking ${ref} not found.` : (e?.response?.data?.detail || "Failed to load booking timeline.")); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [ref]);

  const refresh = async () => {
    const { data } = await api.get(`/admin/bookings/${ref}/timeline`);
    setData(data);
  };

  const isAdmin = data?.viewer_role === "admin" || user?.role === "admin";
  const isLabStaff = isAdmin || data?.viewer_role === "lab_technician" || user?.role === "lab_technician";

  const visibleFilters = FILTERS.filter((f) => isAdmin || !f.adminOnly);

  const filteredEvents = useMemo(() => {
    if (!data?.events) return [];
    let rows = data.events;
    if (filter !== "all") {
      const f = FILTERS.find((x) => x.key === filter);
      if (f?.kinds) rows = rows.filter((e) => f.kinds.includes(e.kind));
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((e) =>
        (e.title || "").toLowerCase().includes(q) ||
        (e.detail || "").toLowerCase().includes(q) ||
        (e.actor || "").toLowerCase().includes(q),
      );
    }
    return rows;
  }, [data, filter, search]);

  return (
    <PortalShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Link to={isAdmin ? "/admin/bookings" : "/lab/dashboard"} className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900" data-testid="booking-timeline-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to {isAdmin ? "Bookings" : "Lab Dashboard"}
        </Link>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-12 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading booking…
          </div>
        ) : error ? (
          <div className="mt-6 rounded-xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-700">
            {error}
          </div>
        ) : !data ? null : (
          <>
            <Header
              summary={data.summary}
              hint={data.next_action_hint}
              summaryBranding={data.summary_branding}
              isAdmin={isAdmin}
              canEditCustomer={isLabStaff}
              onCustomerUpdated={refresh}
            />

            {/* Phase 12 — Partner Network Panels (admin only, read-only) */}
            {isAdmin && (
              <PartnerNetworkPanels reference={data.summary.reference} />
            )}

            {/* Phase 24 — PWS Status Panel (admin only, read-only) */}
            <PWSStatusPanel
              kind="booking"
              identifier={data.summary.reference}
              isAdmin={isAdmin}
            />

            <div className="grid lg:grid-cols-[1fr,300px] gap-6 mt-6">
              {/* Timeline */}
              <section data-testid="booking-timeline">
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    {visibleFilters.map((f) => (
                      <button
                        key={f.key}
                        onClick={() => setFilter(f.key)}
                        data-testid={`timeline-filter-${f.key}`}
                        className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${filter === f.key ? "bg-slate-900 text-white border-slate-900" : "text-slate-600 border-slate-200 hover:border-slate-400"}`}
                      >{f.label}</button>
                    ))}
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search timeline…"
                      data-testid="timeline-search"
                      className="ml-auto text-xs px-3 py-1.5 rounded-full border border-slate-200 focus:border-cyan-500 focus:outline-none w-full sm:w-auto"
                    />
                  </div>
                  {filteredEvents.length === 0 ? (
                    <div className="text-sm text-slate-400 italic py-8 text-center">No events for this filter.</div>
                  ) : (
                    <ol className="relative space-y-3" data-testid="timeline-events">
                      <div className="absolute left-[18px] top-2 bottom-2 w-px bg-slate-100" />
                      {filteredEvents.map((e) => <Event key={e.id} event={e} isAdmin={isAdmin} />)}
                    </ol>
                  )}
                </div>
              </section>

              {/* Sidebar — internal note composer + quick actions */}
              <aside className="space-y-3" data-testid="booking-sidebar">
                <InternalNoteComposer reference={data.summary.reference} onAdded={refresh} />
                <QuickActions actions={data.actions_available} reference={data.summary.reference} isAdmin={isAdmin} />
              </aside>
            </div>

            {isLabStaff && (
              <MessagesSection
                reference={data.summary.reference}
                customer={data.summary.customer || {}}
                onChange={refresh}
              />
            )}
          </>
        )}
      </div>
    </PortalShell>
  );
}

function TechCollectedBanner({ summary }) {
  const [busy, setBusy] = useState(false);
  const paid = !!summary.is_paid || (summary.payment_status || "").toLowerCase() === "paid";
  const sendReminder = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/field-bookings/${summary.reference}/payment-reminder`);
      toast.success(`Payment reminder sent to ${data.sent_to}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Reminder failed"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="booking-tech-collected-banner"
      className={`mt-3 rounded-2xl border p-4 ${paid ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300"}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className={`text-[10px] uppercase tracking-[0.15em] font-bold ${paid ? "text-emerald-700" : "text-amber-700"}`}>
            Technician Collected — No Customer Return Required
          </div>
          <div className="mt-1 text-xs text-slate-700">
            Sample source: <b>{summary.sample_source || "—"}</b>
            {summary.sample_collected_by ? <> · Collected by <b>{summary.sample_collected_by}</b></> : null}
            {summary.sample_collected_at ? <> · {formatDateTimeAU(summary.sample_collected_at)}</> : null}
          </div>
          <div className="mt-1 text-xs">
            {paid
              ? <span className="font-bold text-emerald-700">Payment: Paid — ready for processing when received</span>
              : <span className="font-bold text-amber-700" data-testid="booking-on-hold-payment">ON HOLD — PAYMENT REQUIRED · Do not commence testing</span>}
          </div>
          <div className="mt-1 text-[11px] text-slate-500">
            Customer email: {paid ? "Technician-collected confirmation" : summary.field_payment_link_sent_at ? "Sample-collected + payment link sent" : "Not yet sent"}
            {summary.field_payment_reminder_sent_at ? ` · Reminder sent ${formatDateTimeAU(summary.field_payment_reminder_sent_at)}` : ""}
            {" · Collection instructions: Not required · Return label: Not required · Mail-out kit: Not required"}
          </div>
        </div>
        {!paid && (
          <Button data-testid="booking-send-payment-reminder" size="sm" disabled={busy} onClick={sendReminder}
            className="rounded-full bg-amber-600 hover:bg-amber-700 text-white">
            {busy ? "Sending…" : "Send payment reminder"}
          </Button>
        )}
      </div>
    </div>
  );
}

function Header({ summary, hint, summaryBranding, isAdmin, canEditCustomer, onCustomerUpdated }) {
  const [remindersPaused, setRemindersPaused] = useState(!!summary.invoice_reminders_paused);
  const [shippingRefreshKey, setShippingRefreshKey] = useState(0);
  const cust = summary.customer || {};
  const [editOpen, setEditOpen] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  const showContact = cust.email !== undefined || cust.mobile !== undefined || cust.address !== undefined;
  // Phase 2L-A123 — Admin Mark PM Invoice as Paid (manual). Button
  // appears on the PM · Invoice (account) card ONLY when:
  //   • viewer is admin
  //   • booking is a PM invoice booking
  //   • payment_status is not already paid
  const canMarkPaidManual = (
    isAdmin
    && !!summary.pm_booking
    && (summary.payment_status || "").toLowerCase() !== "paid"
  );
  // Phase 2L-A124 follow-up + Jun 2026 — Admin can re-send the invoice
  // email for ANY invoice/account booking (PM, retail invoice-to-follow,
  // bank transfer), at any time — even after paid or published.
  const canResendInvoice = isAdmin && (
    !!summary.pm_booking
    || (summary.payment_mode || "").toLowerCase() === "invoice"
    || ["bank_transfer", "invoice_account"].includes((summary.payment_method || "").toLowerCase())
  );
  const mp = summary.manual_payment || null;
  // Jun 2026 — payment correction actions
  const [removePaidOpen, setRemovePaidOpen] = useState(false);
  const paidNow = !isBookingNoPaymentRequired(summary) && (
    !!summary.is_paid || (summary.payment_status || "").toLowerCase() === "paid" || !!summary.paid_at
  );
  const unpaidChasable = isAdmin && !paidNow && !isBookingNoPaymentRequired(summary)
    && Number(summary.final_amount_payable ?? summary.total ?? 0) > 0
    && !["cancelled"].includes((summary.status || "").toLowerCase())
    && !["abandoned", "refunded", "not_required"].includes((summary.payment_status || "").toLowerCase());
  // Feb 2026 — retail/any-booking sanctioned EFT mark-paid. Covers the gap
  // where a retail Stripe pay-link was abandoned/never completed but the
  // customer paid by bank transfer instead — previously there was no
  // sanctioned UI for this (only the PM-invoice card had the button), so
  // admins used the generic workflow-status dropdown instead, which only
  // flips `status` and leaves `payment_status` stuck (Lab Queue gates on
  // `payment_status`, so the booking silently never appeared there).
  const canMarkPaidRetailEft = isAdmin
    && !summary.pm_booking
    && !paidNow
    && !isBookingNoPaymentRequired(summary)
    && !["cancelled"].includes((summary.status || "").toLowerCase());
  return (
    <div className="mt-4 rounded-2xl bg-white border border-slate-200 p-5">
      <div className="mb-2 empty:hidden">
        <TankCleanContext reference={summary.reference} fetch testIdPrefix="admin-booking-tank-context" />
      </div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span data-testid="booking-reference" className="font-mono text-xs font-bold tracking-wider text-slate-500">{summary.reference}</span>
            <StatusPill
              label={
                // Phase 2L-A65 — comp / no-charge takes precedence over
                // is_paid so we never render two duplicate "Paid" pills
                // (status pill below also shows "paid" for comp). The
                // canonical helper covers complimentary=true,
                // payment_mode="comp", payment_status="not_required",
                // no_payment_required=true.
                isBookingNoPaymentRequired(summary) ? "Complimentary / No payment required"
                : summary.is_paid ? "Paid"
                : "Awaiting payment"
              }
              tone={
                isBookingNoPaymentRequired(summary) ? "info"
                : summary.is_paid ? "success"
                : "warn"
              }
              testId="booking-payment-pill"
            />
            <StatusPill label={(summary.status || "—").replaceAll("_", " ")} tone="default" testId="booking-status-pill" />
            {/* Feb 2026 — surface the delivery method at the top so
                admins can tell at a glance whether this booking needs
                a Starshipit label (mail_out) or is a drop-off /
                customer-supplied kit. Prevents the "why isn't this in
                labels-to-create?" confusion. */}
            {summary.kit_method && (() => {
              const km = String(summary.kit_method).toLowerCase();
              const short = km === "mail_out"             ? "Mail-out kit"
                          : km === "local_pickup_dropoff" ? "Drop-off / pickup"
                          : km === "existing_bottle"      ? "Own bottle — no kit"
                          : km === "admin_supplied"       ? "Admin-supplied kit"
                          : summary.kit_method;
              const tone = km === "mail_out"             ? "warn"
                         : km === "local_pickup_dropoff" ? "info"
                         : "default";
              return (
                <StatusPill
                  label={short}
                  tone={tone}
                  testId="booking-kit-method-header-pill"
                />
              );
            })()}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900">{summary.test_name || summary.test_id || "Booking"}</h1>
          <p className="text-xs text-slate-500 mt-1">Booked {formatDateTimeAU(summary.created_at)}{summary.paid_at ? ` · Paid ${formatDateAU(summary.paid_at)}` : ""}</p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-cyan-700">Next action</div>
          <div className="text-sm font-semibold text-slate-900 mt-1" data-testid="booking-next-action">{hint || "—"}</div>
        </div>
      </div>

      {summary.collection_method === "technician_collected" && <TechCollectedBanner summary={summary} />}

      {/* Jun 2026 — Admin payment correction actions. Available even on
          published bookings; never touch report/sample/result state. */}
      {isAdmin && (paidNow || unpaidChasable || canMarkPaidRetailEft) && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="booking-payment-actions">
          <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">Payment actions</span>
          {canMarkPaidRetailEft && (
            <button
              type="button"
              data-testid="admin-mark-paid-eft-retail-btn"
              onClick={() => setMarkPaidOpen(true)}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white bg-emerald-600 hover:bg-emerald-500 rounded-full px-3 py-1.5 shadow-sm transition-colors"
            >
              <Check className="w-3.5 h-3.5" /> Mark paid — EFT / bank transfer
            </button>
          )}
          {paidNow && (
            <button
              type="button"
              data-testid="admin-remove-paid-status-btn"
              onClick={() => setRemovePaidOpen(true)}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-rose-700 bg-white hover:bg-rose-50 border border-rose-300 rounded-full px-3 py-1.5 shadow-sm transition-colors"
            >
              <X className="w-3.5 h-3.5" /> Remove paid status
            </button>
          )}
          {unpaidChasable && (summary.customer || {}).email && (
            <ResendPayLinkInline reference={summary.reference} lastSentAt={summary.payment_link_sent_at} />
          )}
          {unpaidChasable && (
            <AdjustAmountInline reference={summary.reference} currentTotal={summary.total} />
          )}
          {!unpaidChasable && (summary.customer || {}).email && (
            <ResendConfirmationInline reference={summary.reference} lastResentAt={summary.confirmation_resent_at} />
          )}
          {canResendInvoice && !summary.pm_booking && (
            <button
              type="button"
              data-testid="admin-resend-invoice-retail-btn"
              onClick={() => setResendOpen(true)}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-cyan-700 bg-white hover:bg-cyan-50 border border-cyan-300 rounded-full px-3 py-1.5 shadow-sm transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Resend invoice
            </button>
          )}
        </div>
      )}
      <RemovePaidStatusDialog
        open={removePaidOpen}
        onClose={() => setRemovePaidOpen(false)}
        summary={summary}
        onRemoved={onCustomerUpdated}
      />

      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">Customer details</div>
          {canEditCustomer && (
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              data-testid="customer-edit-open"
              aria-label="Edit customer details"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-800 hover:text-white bg-cyan-50 hover:bg-cyan-600 rounded-full px-3 py-1.5 border border-cyan-300 hover:border-cyan-700 shadow-sm transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-3 text-xs" data-testid="booking-customer-summary">
          <Row label="Customer">{cust.name || "—"}</Row>
          <Row label="Suburb">{cust.suburb || "—"}</Row>
          {showContact && <Row label="Email">{cust.email || "—"}</Row>}
          {showContact && <Row label="Mobile">{cust.mobile || "—"}</Row>}
          {showContact && <Row label="Address" className="sm:col-span-2">{cust.address || "—"}</Row>}
          {/* Phase 2L-A31 — sample identification (optional). Surfaced
              here so admins reading the booking can see the same
              values the COA, sample labels and lab queue use. */}
          {(summary.customer_sample_reference || summary.sample_point) && (
            <>
              {summary.customer_sample_reference && (
                <Row label="Sample reference" data-testid="booking-customer-sample-reference">
                  {summary.customer_sample_reference}
                </Row>
              )}
              {summary.sample_point && (
                <Row label="Sample taken from" data-testid="booking-sample-point">
                  {summary.sample_point}
                </Row>
              )}
            </>
          )}
        </div>
      </div>

      {/* Phase 2 + 3 + 4 — Aware Partner Network attribution chip.
          Renders for partner_link / stored_partner_attribution / region_allocation
          even when partner_id is blank (the review-required path stamps
          booking_source='region_allocation' with NULL partner_id so the
          admin can resolve the ambiguity from the timeline). */}
      {(summary.partner_id || summary.booking_source === "region_allocation") && (
        <div
          data-testid="booking-partner-attribution-block"
          className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4"
        >
          <div className="flex items-center gap-2 mb-1">
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-amber-800">
              Partner attribution
            </div>
            <span
              data-testid="booking-allocation-method-chip"
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-900 uppercase tracking-wider"
            >
              {(summary.allocation_method || summary.booking_source || "partner_link").replaceAll("_", " ")}
            </span>
            {summary.booking_source === "stored_partner_attribution" && (
              <span
                data-testid="booking-attribution-source-chip"
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-cyan-100 text-cyan-900 uppercase tracking-wider"
              >
                Returned via 90-day cookie
              </span>
            )}
            {summary.booking_source === "partner_link" && (
              <span
                data-testid="booking-attribution-source-chip"
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-900 uppercase tracking-wider"
              >
                Fresh click
              </span>
            )}
            {summary.booking_source === "region_allocation" && summary.allocation_method === "region_match" && (
              <span
                data-testid="booking-attribution-source-chip"
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-indigo-100 text-indigo-900 uppercase tracking-wider"
              >
                Region auto-allocated
              </span>
            )}
            {summary.booking_source === "region_allocation" && summary.allocation_method === "region_match_review_required" && (
              <span
                data-testid="booking-attribution-source-chip"
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-rose-100 text-rose-900 uppercase tracking-wider"
              >
                Region match · admin review required
              </span>
            )}
          </div>
          <div className="text-sm text-slate-700 mb-2">
            {summary.booking_source === "region_allocation" ? (
              summary.allocation_method === "region_match_review_required" ? (
                <>
                  Multiple eligible PWS franchisees cover this region.
                  <span className="text-slate-500"> Admin must review and assign a partner manually.</span>
                </>
              ) : (
                <>
                  Auto-allocated to PWS franchisee{" "}
                  <code
                    data-testid="booking-partner-slug"
                    className="px-1.5 py-0.5 rounded bg-white border border-amber-200 text-[11px] font-mono text-amber-900"
                  >
                    {summary.partner_slug || "—"}
                  </code>
                  <span className="text-slate-500"> · partner_id </span>
                  <code
                    data-testid="booking-partner-id"
                    className="px-1.5 py-0.5 rounded bg-white border border-amber-200 text-[11px] font-mono text-slate-700"
                  >
                    {summary.partner_id || "—"}
                  </code>
                </>
              )
            ) : (
              <>
                Booking landed via partner link{" "}
                <code
                  data-testid="booking-partner-slug"
                  className="px-1.5 py-0.5 rounded bg-white border border-amber-200 text-[11px] font-mono text-amber-900"
                >
                  {summary.partner_slug || "—"}
                </code>
                <span className="text-slate-500"> · partner_id </span>
                <code
                  data-testid="booking-partner-id"
                  className="px-1.5 py-0.5 rounded bg-white border border-amber-200 text-[11px] font-mono text-slate-700"
                >
                  {summary.partner_id}
                </code>
              </>
            )}
          </div>
          {summary.booking_source === "region_allocation" && (
            <div
              data-testid="booking-region-allocation-detail"
              className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2 pt-3 border-t border-amber-200/60 text-xs"
            >
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Match confidence</div>
                <div className="text-slate-700" data-testid="booking-region-match-confidence">
                  {summary.allocation_match_confidence || "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Matched postcode</div>
                <div className="text-slate-700" data-testid="booking-region-matched-postcode">
                  {summary.allocation_matched_postcode || "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Matched suburb</div>
                <div className="text-slate-700" data-testid="booking-region-matched-suburb">
                  {summary.allocation_matched_suburb || "—"}
                </div>
              </div>
              {Array.isArray(summary.allocation_candidate_partner_ids) && summary.allocation_candidate_partner_ids.length > 0 && (
                <div className="sm:col-span-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">
                    Eligible candidate partner{summary.allocation_candidate_partner_ids.length === 1 ? "" : "s"}
                  </div>
                  <div
                    className="text-slate-700 text-[11px] font-mono break-all"
                    data-testid="booking-region-candidate-partner-ids"
                  >
                    {summary.allocation_candidate_partner_ids.join(" · ")}
                  </div>
                </div>
              )}
            </div>
          )}
          {summary.booking_source === "stored_partner_attribution" && (
            <div
              data-testid="booking-attribution-history"
              className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2 pt-3 border-t border-amber-200/60 text-xs"
            >
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">First click</div>
                <div className="text-slate-700" data-testid="booking-first-click-at">
                  {summary.partner_first_click_at
                    ? new Date(summary.partner_first_click_at).toLocaleString("en-AU")
                    : "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Last click</div>
                <div className="text-slate-700" data-testid="booking-last-click-at">
                  {summary.partner_last_click_at
                    ? new Date(summary.partner_last_click_at).toLocaleString("en-AU")
                    : "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Expires</div>
                <div className="text-slate-700" data-testid="booking-attribution-expires-at">
                  {summary.partner_attribution_expires_at
                    ? new Date(summary.partner_attribution_expires_at).toLocaleString("en-AU")
                    : "—"}
                </div>
              </div>
              {summary.partner_source_url && (
                <div className="sm:col-span-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Original source URL</div>
                  <div className="text-slate-700 break-all" data-testid="booking-partner-source-url">
                    {summary.partner_source_url}
                  </div>
                </div>
              )}
              {summary.partner_utm && Object.keys(summary.partner_utm).length > 0 && (
                <div className="sm:col-span-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">UTM</div>
                  <div className="text-slate-700 text-[11px] font-mono break-all" data-testid="booking-partner-utm">
                    {Object.entries(summary.partner_utm).map(([k, v]) => `${k}=${v}`).join(" · ")}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Feb 2026 — Manual allocate / re-allocate button. Sits at
              the bottom of the attribution block so it works for both
              needs-review bookings (no partner yet) and confidently-
              attributed ones (mis-linked correction). */}
          <div className="mt-3 pt-3 border-t border-amber-200/60">
            <AllocatePartnerButton
              reference={summary.reference}
              currentPartnerId={summary.partner_id}
              currentPartnerSlug={summary.partner_slug}
              bookingSummary={summary}
              onAllocated={onCustomerUpdated}
            />
          </div>
        </div>
      )}

      {/* When no attribution block exists (walk-in bookings, admin-
          created bookings without any partner) still show the Allocate
          button so admins can attach a partner post-hoc for reporting. */}
      {(!summary.partner_id && summary.booking_source !== "region_allocation") && (
        <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4">
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-600 mb-1">
            Partner attribution
          </div>
          <div className="text-xs text-slate-500 mb-3">
            No partner attribution on this booking. Allocate to a partner if this was a referral you&apos;d like credited in the ledger.
          </div>
          <AllocatePartnerButton
            reference={summary.reference}
            currentPartnerId={null}
            currentPartnerSlug={null}
            bookingSummary={summary}
            onAllocated={onCustomerUpdated}
          />
        </div>
      )}

      {/* Phase 7 — Summary Report white-label branding chip.
          Shown alongside the partner attribution block so admins can see at a
          glance whether the customer's Summary PDF was/will be rendered with
          partner branding or fell back to Aware, and why. The COA is ALWAYS
          SCWT-branded regardless of this state. */}
      {summaryBranding && (
        <div
          data-testid="booking-white-label-block"
          className={`mt-4 rounded-xl p-4 border ${
            summaryBranding.summary_branding_mode === "partner_white_label"
              ? "bg-emerald-50 border-emerald-200"
              : "bg-slate-50 border-slate-200"
          }`}
        >
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-700">
              Summary Report branding
            </div>
            {summaryBranding.summary_branding_mode === "partner_white_label" ? (
              <span
                data-testid="booking-white-label-mode-chip"
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-200 text-emerald-900 uppercase tracking-wider"
              >
                Partner white-label
              </span>
            ) : (
              <span
                data-testid="booking-white-label-mode-chip"
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-slate-200 text-slate-800 uppercase tracking-wider"
              >
                Aware branding
              </span>
            )}
            <span
              data-testid="booking-white-label-version-chip"
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-white border border-slate-200 text-slate-600 uppercase tracking-wider"
            >
              v{(summaryBranding.white_label_version || "v1").replace(/^v/, "")}
            </span>
          </div>
          <div className="text-xs text-slate-700">
            {summaryBranding.summary_branding_mode === "partner_white_label" ? (
              <>
                Summary Report will be rendered with{" "}
                <strong data-testid="booking-white-label-partner-name">
                  {summaryBranding.summary_partner_name || "the partner"}
                </strong>
                {"'s"} branding. The Certificate of Analysis remains Aware Water
                Testing-branded.
              </>
            ) : (
              <>
                Summary Report uses the default Aware Water Testing branding.
                {summaryBranding.white_label_fallback_reason && (
                  <>
                    {" "}Fallback reason:{" "}
                    <code
                      data-testid="booking-white-label-fallback-reason"
                      className="px-1.5 py-0.5 rounded bg-white border border-slate-200 text-[11px] font-mono text-slate-700"
                    >
                      {summaryBranding.white_label_fallback_reason}
                    </code>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Phase 9 — Pricing audit panel */}
      {summary.pricing_mode && (
        <PricingPanel summary={summary} />
      )}

      {/* Phase 8 — Mail-Out Sample Kit panel (admin controls + audit) */}
      {summary.kit_method && (
        <KitPanel summary={summary} onChanged={onCustomerUpdated} />
      )}

      {/* Phase 2L-A144 — Paid posted sample kit fulfilment panel.
          The panel fetches its own state via GET /admin/bookings/{ref}/posted-kit
          and self-hides when the booking has no posted-kit state
          (i.e. `posted_kit_paid_at_creation` and
          `shipping_integration_status` are both falsy). That lets us
          render this panel without needing the timeline serializer to
          project A144 fields into `summary` — a booking that gets
          flipped into posted-kit via admin trigger will surface the
          panel on the next detail-page load. */}
      <PostedKitFulfilmentPanel
        reference={summary.reference}
        onChanged={() => { onCustomerUpdated?.(); setShippingRefreshKey((k) => k + 1); }}
      />

      {/* Feb-2026: Starshipit / Australia Post shipping panel.
          Self-hides when the booking isn't mail_out and has no
          Starshipit history. Fetches its own state via
          GET /api/admin/bookings/{ref}/shipping. `refreshKey` bumps
          after the posted-kit address panel above saves, so the
          eligibility banner/buttons here update without a manual
          page reload (Nov 2026, SCWT-34E6D6). */}
      <StarshipitShippingPanel reference={summary.reference} refreshKey={shippingRefreshKey} />

      {/* Phase 2L-A116 + A121 — PM invoice booking attribution */}
      {summary.payment_mode === "invoice" && !summary.pm_booking && (
        <div
          data-testid="booking-invoice-to-follow-block"
          className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4"
        >
          <div className="flex items-center justify-between mb-2 gap-2">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700">
              Invoice to follow · bank transfer
            </div>
            <button
              type="button"
              data-testid="admin-download-invoice-to-follow-pdf"
              onClick={() => fetchAndOpenAuthed({
                path: `/bookings/${summary.reference}/invoice.pdf`,
                mode: "open",
                loadingMessage: "Opening invoice PDF…",
              })}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-700 hover:text-amber-900 hover:underline"
            >
              <Download className="w-3.5 h-3.5" /> Download invoice PDF
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 text-xs">
            <Row label="Invoice email sent">
              <span data-testid="booking-invoice-to-follow-sent-at">
                {summary.invoice_sent_at
                  ? formatDateTimeAU(summary.invoice_sent_at)
                  : "Not sent (staff opted out or send failed — see timeline)"}
              </span>
            </Row>
            <Row label="Recipient">
              <span data-testid="booking-invoice-to-follow-recipient">
                {summary.invoice_recipient || "—"}
              </span>
            </Row>
            <Row label="Customer email status">
              <span data-testid="booking-invoice-to-follow-email-status">
                {summary.invoice_email_status || "—"}
              </span>
            </Row>
            <Row label="Team email status">
              <span data-testid="booking-invoice-to-follow-team-status">
                {summary.invoice_team_status || "—"}
              </span>
            </Row>
            <Row label="Payment terms">
              <span data-testid="booking-invoice-terms">
                {summary.invoice_terms_days ? `${summary.invoice_terms_days} days` : "—"}
              </span>
            </Row>
            <Row label="Due date">
              <span data-testid="booking-invoice-due-at">
                {summary.invoice_due_at ? formatDateTimeAU(summary.invoice_due_at) : "—"}
              </span>
            </Row>
            <Row label="Payment reminders">
              <span data-testid="booking-invoice-reminders" className="inline-flex items-center gap-2">
                {remindersPaused
                  ? "Paused"
                  : `${(summary.invoice_reminder_stages || []).length} sent · auto (due, +7, +14, +30 days)`}
                <button
                  type="button"
                  data-testid="booking-invoice-reminders-toggle"
                  onClick={async () => {
                    try {
                      await api.patch(`/admin/bookings/${summary.reference}/invoice-reminders`,
                        { paused: !remindersPaused });
                      toast.success(remindersPaused ? "Reminders resumed" : "Reminders paused");
                      setRemindersPaused(!remindersPaused);
                    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
                  }}
                  className="text-[10px] font-bold uppercase text-amber-700 underline hover:text-amber-900"
                >
                  {remindersPaused ? "Resume" : "Pause"}
                </button>
              </span>
            </Row>
          </div>
        </div>
      )}

      {(summary.pm_booking || summary.payment_method === "invoice_account" || summary.purchase_order_number) && (
        <div
          data-testid="booking-pm-invoice-block"
          className="mt-4 rounded-xl bg-cyan-50 border border-cyan-200 p-4"
        >
          <div className="flex items-center justify-between mb-2 gap-2">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">
              Property Manager · Invoice (account)
            </div>
            <div className="flex items-center gap-2">
              {/* Phase 2L-A124 — admin convenience PDF link using
                  the logged-in route (no signed token needed). */}
              <a
                data-testid="admin-download-invoice-pdf"
                href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/bookings/${summary.reference}/invoice.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-cyan-700 hover:text-cyan-800 hover:underline"
              >
                <Download className="w-3.5 h-3.5" /> Download PDF
              </a>
              {canResendInvoice && (
                <button
                  type="button"
                  data-testid="admin-resend-invoice-email-btn"
                  onClick={() => setResendOpen(true)}
                  title="Re-send the invoice email to the agency PM"
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-cyan-700 hover:text-white bg-white hover:bg-cyan-600 border border-cyan-300 hover:border-cyan-700 rounded-full px-3 py-1.5 shadow-sm transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Re-send invoice email
                </button>
              )}
              {canMarkPaidManual && (
                <button
                  type="button"
                  data-testid="admin-mark-pm-invoice-paid-btn"
                  onClick={() => setMarkPaidOpen(true)}
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white bg-emerald-600 hover:bg-emerald-500 rounded-full px-3 py-1.5 shadow-sm transition-colors"
                >
                  <Check className="w-3.5 h-3.5" /> Mark invoice as paid
                </button>
              )}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 text-xs">
            <Row label="Booking authority">
              <span data-testid="booking-managed-by-name">
                {summary.managed_by_partner_name || summary.created_by_partner_name || "—"}
              </span>
            </Row>
            <Row label="Property Manager">
              <span data-testid="booking-pm-contact">
                {[summary.created_by_name, summary.created_by_email, summary.created_by_phone].filter(Boolean).join(" · ") || "—"}
              </span>
            </Row>
            <Row label="Payment method">
              <span data-testid="booking-payment-method">
                {summary.payment_method === "invoice_account" ? "Invoice (account)" : (summary.payment_method || "—")}
              </span>
            </Row>
            <Row label="PO number">
              <span
                data-testid="booking-purchase-order-number"
                className="font-mono font-semibold text-slate-900"
              >
                {summary.purchase_order_number || "—"}
              </span>
            </Row>
            {summary.payment_terms === "30_days" && (
              <>
                <Row label="Payment terms">
                  <span
                    data-testid="booking-payment-terms"
                    className="font-semibold text-slate-900"
                  >
                    Net 30 — payable within 30 days
                  </span>
                </Row>
                <Row label="Due date">
                  <span
                    data-testid="booking-payment-due-at"
                    className="font-semibold text-slate-900"
                  >
                    {summary.payment_due_at ? formatDateTimeAU(summary.payment_due_at) : "—"}
                  </span>
                </Row>
              </>
            )}
            <Row label="Invoice sent">
              {summary.invoice_sent_at ? formatDateTimeAU(summary.invoice_sent_at) : "—"}
            </Row>
            <Row label="Invoice email status">
              <span data-testid="booking-invoice-email-status">
                {summary.invoice_email_status || "—"}
              </span>
            </Row>
            {summary.invoice_resent_at && (
              <Row label="Last re-sent">
                <span
                  data-testid="booking-invoice-resent-at"
                  className="font-semibold text-cyan-700"
                >
                  {formatDateTimeAU(summary.invoice_resent_at)}
                </span>
              </Row>
            )}
            <Row label="Invoice recipient" className="sm:col-span-2">
              {summary.invoice_recipient || "—"}
            </Row>

            {/* Feb 2026 — customer notification audit trail for the
                sample-label PDF, mail-out kit instructions, and the
                outbound tracking email. Makes it obvious what the
                customer actually received. */}
            <Row label="Sample-labels PDF emailed">
              <span data-testid="booking-notif-sample-labels-pdf">
                {summary.sample_label_pdf_emailed_at
                  ? `Yes · ${formatDateTimeAU(summary.sample_label_pdf_emailed_at)}`
                  : (summary.kit_method === "mail_out"
                      ? "Skipped (mail-out kit)"
                      : "—")}
              </span>
            </Row>
            <Row label="Mail-out kit instructions emailed">
              <span data-testid="booking-notif-mail-out-instructions">
                {summary.mail_out_kit_instructions_sent_at
                  ? `Yes · ${formatDateTimeAU(summary.mail_out_kit_instructions_sent_at)}`
                  : (summary.kit_method === "mail_out" ? "Pending" : "—")}
                {summary.mail_out_kit_instructions_send_status
                  && summary.mail_out_kit_instructions_send_status !== "ok" && (
                  <span className="ml-2 text-[11px] font-semibold text-rose-700">
                    ({summary.mail_out_kit_instructions_send_status})
                  </span>
                )}
              </span>
            </Row>
            <Row label="Outbound tracking email">
              <span data-testid="booking-notif-outbound-tracking">
                {summary.outbound_tracking_email_sent_at
                  || summary.starshipit_tracking_email_sent_at
                  ? `Yes · ${formatDateTimeAU(summary.outbound_tracking_email_sent_at || summary.starshipit_tracking_email_sent_at)}`
                  : (summary.kit_method === "mail_out"
                      ? (summary.outbound_tracking_number ? "Pending" : "—")
                      : "—")}
              </span>
            </Row>
            {summary.site_contact && (summary.site_contact.name || summary.site_contact.phone) && (
              <Row label="Tenant / site contact" className="sm:col-span-2">
                <span data-testid="booking-site-contact">
                  {[summary.site_contact.name, summary.site_contact.phone, summary.site_contact.email].filter(Boolean).join(" · ")}
                </span>
                {summary.site_contact.access_notes && (
                  <div className="mt-1 text-[11px] italic text-slate-600">
                    {summary.site_contact.access_notes}
                  </div>
                )}
              </Row>
            )}
            {/* Phase 2L-A123 — manual mark-paid receipt fields. Rendered
                whenever a manual_payment audit block is present on the
                booking (admin already saw the button to create it). */}
            {mp && (
              <>
                <Row label="Paid on">
                  <span
                    data-testid="booking-manual-paid-at"
                    className="font-semibold text-emerald-700"
                  >
                    {mp.paid_at ? formatDateTimeAU(mp.paid_at) : "—"}
                  </span>
                </Row>
                <Row label="Amount paid">
                  <span
                    data-testid="booking-manual-paid-amount"
                    className="font-semibold text-slate-900"
                  >
                    {typeof mp.amount_paid === "number"
                      ? `$${mp.amount_paid.toFixed(2)} AUD`
                      : "—"}
                  </span>
                </Row>
                <Row label="Payment method">
                  <span
                    data-testid="booking-manual-paid-method"
                    className="font-semibold text-slate-900 capitalize"
                  >
                    {(mp.payment_method || "").replace("_", " ") || "—"}
                  </span>
                </Row>
                <Row label="Marked paid by">
                  <span data-testid="booking-manual-paid-by">
                    {mp.marked_by_email || "—"}
                  </span>
                </Row>
                {mp.payment_reference && (
                  <Row label="Payment reference">
                    <span
                      data-testid="booking-manual-paid-reference"
                      className="font-mono text-slate-900"
                    >
                      {mp.payment_reference}
                    </span>
                  </Row>
                )}
                {mp.notes && (
                  <Row label="Notes" className="sm:col-span-2">
                    <span
                      data-testid="booking-manual-paid-notes"
                      className="italic text-slate-700"
                    >
                      {mp.notes}
                    </span>
                  </Row>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {(canMarkPaidManual || canMarkPaidRetailEft) && (
        <MarkPaidDialog
          open={markPaidOpen}
          onClose={() => setMarkPaidOpen(false)}
          reference={summary.reference}
          defaultAmount={summary.total || cust.total || 0}
          onMarkedPaid={() => { setMarkPaidOpen(false); onCustomerUpdated?.(); }}
        />
      )}

      {canResendInvoice && (
        <ResendInvoiceDialog
          open={resendOpen}
          onClose={() => setResendOpen(false)}
          reference={summary.reference}
          recipient={summary.pm_booking ? (summary.invoice_recipient || cust.email) : (cust.email || summary.invoice_recipient)}
          onResent={() => { setResendOpen(false); onCustomerUpdated?.(); }}
        />
      )}

      {canEditCustomer && (
        <CustomerDetailsEditModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          reference={summary.reference}
          customer={cust}
          postedKitAddress={summary.posted_kit_address}
          onSaved={() => { setEditOpen(false); onCustomerUpdated?.(); }}
        />
      )}
    </div>
  );
}

function Row({ label, children, className = "" }) {
  return (
    <div className={`${className}`}>
      <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">{label}</div>
      <div className="text-slate-900">{children}</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Phase 9 — Pricing audit panel (admin BookingDetail)
// ════════════════════════════════════════════════════════════════════
const PRICING_MODE_LABELS = {
  local_scwt:        { label: "Local Aware",       bg: "bg-slate-200",   fg: "text-slate-800" },
  national_partner:  { label: "National Partner", bg: "bg-cyan-100",    fg: "text-cyan-900" },
  admin_override:    { label: "Admin Override",   bg: "bg-amber-100",   fg: "text-amber-900" },
};
const PRICE_SOURCE_LABELS = {
  direct_scwt:                  "Direct Aware",
  partner_link:                 "Partner link",
  stored_partner_attribution:   "Stored attribution (90d)",
  region_allocation:            "Region allocation",
  admin_override:               "Admin override",
};


// Feb 2026 — Manual partner allocation for bookings.
//   Renders a compact "Allocate to partner" / "Change partner" button
//   inside the attribution block. Clicking opens a modal with a
//   typeahead partner picker (filtered to ACTIVE PWS franchisees by
//   default, with an escape hatch for backfilling inactive attribution
//   for reporting only). Saves via
//   POST /api/admin/bookings/{reference}/allocate-partner.
function AllocatePartnerButton({ reference, currentPartnerId, currentPartnerSlug, bookingSummary, onAllocated }) {
  const [open, setOpen] = useState(false);
  const hasCurrent = !!currentPartnerId;
  return (
    <>
      <button
        type="button"
        data-testid="booking-allocate-partner-btn"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-cyan-600 bg-white text-cyan-700 hover:bg-cyan-50 transition-colors"
      >
        <Users className="w-3.5 h-3.5" />
        {hasCurrent ? "Change partner" : "Allocate to partner"}
      </button>
      {open && (
        <AllocatePartnerModal
          reference={reference}
          currentPartnerId={currentPartnerId}
          currentPartnerSlug={currentPartnerSlug}
          bookingSummary={bookingSummary}
          onClose={() => setOpen(false)}
          onDone={() => { setOpen(false); onAllocated && onAllocated(); }}
        />
      )}
    </>
  );
}


function AllocatePartnerModal({ reference, currentPartnerId, currentPartnerSlug, bookingSummary, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [partners, setPartners] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  // Feb 2026 Option C — Admin can flag "partner already handed
  // customer a kit". Only offered for mail-out bookings that haven't
  // been dispatched yet and aren't already marked partner-supplied.
  const [partnerSuppliedKit, setPartnerSuppliedKit] = useState(false);
  const kitMethod = (bookingSummary?.kit_method || "").toLowerCase();
  const kitSource = (bookingSummary?.kit_source || "").toLowerCase();
  const isMailOut = kitMethod === "mail_out";
  const alreadyPartnerSupplied = kitSource === "partner_supplied";
  const alreadyDispatched = !!bookingSummary?.fulfilment_triggered_at;
  const canOfferPartnerSuppliedKit = isMailOut && !alreadyPartnerSupplied && !alreadyDispatched;

  useEffect(() => {
    let cancelled = false;
    setLoadingList(true);
    const params = new URLSearchParams({ limit: "300", partner_type: "pws_franchisee" });
    if (q) params.set("q", q);
    api.get(`/admin/pws-partners?${params.toString()}`)
      .then(({ data }) => {
        if (cancelled) return;
        const items = data?.items || data || [];
        const active   = items.filter((p) => p.pws_partner_status === "active");
        const inactive = items.filter((p) => p.pws_partner_status !== "active");
        setPartners(includeInactive ? [...active, ...inactive] : active);
      })
      .catch(() => { if (!cancelled) setPartners([]); })
      .finally(() => { if (!cancelled) setLoadingList(false); });
    return () => { cancelled = true; };
  }, [q, includeInactive]);

  useEffect(() => {
    let cancelled = false;
    api.get(`/admin/bookings/${reference}/allocation-audit`)
      .then(({ data }) => { if (!cancelled) setHistory(data?.rows || []); })
      .catch(() => { if (!cancelled) setHistory([]); });
    return () => { cancelled = true; };
  }, [reference]);

  const submit = async () => {
    if (!selected) { toast.error("Choose a partner first"); return; }
    if ((note || "").trim().length < 3) {
      toast.error("Add a note (min 3 chars) — this goes into the audit log."); return;
    }
    setBusy(true);
    try {
      const isActive = selected.pws_partner_status === "active";
      const body = { partner_id: selected.id, note: note.trim() };
      if (!isActive) body.include_inactive = true;
      if (partnerSuppliedKit && canOfferPartnerSuppliedKit) {
        body.partner_supplied_kit = true;
      }
      const { data } = await api.post(
        `/admin/bookings/${reference}/allocate-partner`, body,
      );
      // Compose the toast — special-case the partner-supplied-kit
      // effect so admins see the reimbursement + inventory outcome
      // rather than a plain "allocated" message.
      const effects = data?.partner_supplied_effects;
      if (data?.partner_supplied_kit && effects?.applied) {
        if (effects.reimbursement_ledger_id) {
          toast.success(
            `Allocated to ${selected.business_name}. Kit marked partner-supplied — ` +
            `reimbursement queued ($${((effects.reimbursement_amount_cents || 0) / 100).toFixed(2)}, ` +
            `${effects.reimbursement_state}) and 1 kit deducted from shed.`,
          );
        } else {
          toast.success(
            `Allocated to ${selected.business_name}. Kit marked partner-supplied — ` +
            `1 kit deducted from shed. Reimbursement will be created once payment lands.`,
          );
        }
      } else {
        toast.success(
          data?.would_notify
            ? `Allocated to ${selected.business_name}. Partner will be notified.`
            : `Allocated to ${selected.business_name} (inactive — attribution backfilled only).`,
        );
      }
      onDone && onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.message || "Allocation failed");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const isNewAttribution = !currentPartnerId;
  const isReattributing  = !!currentPartnerId && selected && selected.id !== currentPartnerId;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div
        data-testid="booking-allocate-partner-modal"
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mt-16 mb-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-lg font-bold text-slate-900">
              {isNewAttribution ? "Allocate booking to partner" : "Change booking partner"}
            </div>
            <div className="text-xs text-slate-500">
              Booking <span className="font-mono">{reference}</span>
              {currentPartnerSlug && <> · currently <span className="font-mono">{currentPartnerSlug}</span></>}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="booking-allocate-partner-close"
            className="p-1.5 rounded hover:bg-slate-100"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {isReattributing && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
              <div className="font-semibold flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> Re-attributing a booking
              </div>
              <div className="mt-0.5 opacity-90">
                This booking already has a partner. Changing it will write an audit row and re-run commission recalculation for paid bookings.
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Input
              data-testid="booking-allocate-partner-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by business name, slug or region…"
              className="h-9 flex-1"
            />
            <label className="inline-flex items-center gap-1.5 text-xs whitespace-nowrap px-3 py-2 rounded-md border border-slate-200 bg-white cursor-pointer">
              <input
                type="checkbox"
                data-testid="booking-allocate-partner-include-inactive"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
              />
              Show inactive
            </label>
          </div>

          <div
            data-testid="booking-allocate-partner-list"
            className="rounded-lg border border-slate-200 max-h-56 overflow-y-auto bg-slate-50"
          >
            {loadingList ? (
              <div className="p-4 text-xs text-slate-500">Loading…</div>
            ) : partners.length === 0 ? (
              <div className="p-4 text-xs text-slate-500">No partners match.</div>
            ) : (
              partners.map((p) => {
                const active = p.pws_partner_status === "active";
                const isSel = selected?.id === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSelected(p)}
                    data-testid={`booking-allocate-partner-option-${p.id}`}
                    className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 flex items-center gap-2 text-xs transition-colors ${
                      isSel ? "bg-cyan-50 ring-1 ring-cyan-500 relative z-10" : "bg-white hover:bg-slate-50"
                    }`}
                  >
                    <div className={`w-2 h-2 rounded-full ${active ? "bg-emerald-500" : "bg-slate-400"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{p.business_name || "—"}</div>
                      <div className="text-slate-500 text-[11px] font-mono">
                        {p.partner_slug || "—"}{p.franchise_region ? ` · ${p.franchise_region}` : ""}
                      </div>
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                      active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"
                    }`}>
                      {p.pws_partner_status || "draft"}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          {selected && (
            <div
              data-testid="booking-allocate-partner-selected"
              className={`rounded-lg px-3 py-2 text-xs ${
                selected.pws_partner_status === "active"
                  ? "bg-emerald-50 border border-emerald-200 text-emerald-900"
                  : "bg-amber-50 border border-amber-200 text-amber-900"
              }`}
            >
              <div className="font-semibold">
                Selected: {selected.business_name} <span className="font-mono opacity-80">({selected.partner_slug || "no-slug"})</span>
              </div>
              <div className="opacity-90 mt-0.5">
                {selected.pws_partner_status === "active"
                  ? "Active partner — allocation will notify them (subject to notification settings)."
                  : `${selected.pws_partner_status} partner — attribution will be backfilled for reporting only. NO notification will be sent.`}
              </div>
            </div>
          )}

          {/* Feb 2026 Option C — "Partner already supplied kit" checkbox.
              Only offered for un-dispatched mail-out bookings that aren't
              already marked partner-supplied. Ticking flips kit_source,
              cancels the mail-out, decrements partner inventory, and (for
              paid bookings) writes the $29 reimbursement ledger row. */}
          {selected && canOfferPartnerSuppliedKit && (
            <div
              data-testid="booking-allocate-partner-partner-supplied-kit-block"
              className="rounded-lg border border-cyan-200 bg-cyan-50/60 p-3"
            >
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="booking-allocate-partner-partner-supplied-kit"
                  checked={partnerSuppliedKit}
                  onChange={(e) => setPartnerSuppliedKit(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-cyan-400 text-cyan-600 focus:ring-cyan-500"
                />
                <div className="text-xs text-slate-800">
                  <div className="font-semibold text-cyan-900">
                    Partner already supplied kit
                  </div>
                  <div className="opacity-90 mt-0.5">
                    Cancel the pending mail-out, deduct 1 kit from the partner&apos;s shed,
                    and queue a $29 reimbursement (auto-eligible for card payments,
                    held for review for other payment sources).
                  </div>
                </div>
              </label>
              {partnerSuppliedKit && selected.kit_inventory_available != null && (
                <div className="mt-2 text-[11px] text-slate-600">
                  Current shed: <span className="font-mono font-bold">{selected.kit_inventory_available}</span> kits — will drop to <span className="font-mono font-bold">{Math.max(0, selected.kit_inventory_available - 1)}</span>.
                </div>
              )}
            </div>
          )}
          {selected && !canOfferPartnerSuppliedKit && isMailOut && (alreadyDispatched || alreadyPartnerSupplied) && (
            <div
              data-testid="booking-allocate-partner-partner-supplied-kit-locked"
              className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600"
            >
              {alreadyPartnerSupplied
                ? "This booking is already marked as partner-supplied — inventory & reimbursement already recorded."
                : "Mail-out kit has already been dispatched — cannot retroactively mark as partner-supplied. Process a refund/credit manually if needed."}
            </div>
          )}

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">
              Note (required, ≥ 3 chars — kept in the audit log)
            </Label>
            <Textarea
              data-testid="booking-allocate-partner-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="e.g. Customer confirmed they came via John at PWS Cooloola on the phone."
              className="mt-1 text-sm"
            />
          </div>

          {history.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-1">
                Allocation history ({history.length})
              </div>
              <div
                data-testid="booking-allocate-partner-history"
                className="rounded-lg border border-slate-200 bg-white max-h-32 overflow-y-auto divide-y divide-slate-100"
              >
                {history.slice(0, 8).map((row) => (
                  <div key={row.id} className="px-3 py-1.5 text-[11px]">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500 text-[10px]">
                        {new Date(row.created_at).toLocaleString("en-AU")}
                      </span>
                      <span className="font-mono text-slate-700">
                        {row.old_partner_slug || "—"} → {row.new_partner_slug || "—"}
                      </span>
                      {row.would_notify
                        ? <span className="text-emerald-700 text-[9px] font-bold uppercase">notified</span>
                        : <span className="text-amber-700 text-[9px] font-bold uppercase">backfill</span>}
                    </div>
                    <div className="text-slate-600 mt-0.5">
                      {row.note} <span className="text-slate-400">— {row.actor_email}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="booking-allocate-partner-cancel">Cancel</Button>
          <Button
            data-testid="booking-allocate-partner-submit"
            onClick={submit}
            disabled={busy || !selected || (note || "").trim().length < 3}
            className="bg-cyan-700 hover:bg-cyan-800 text-white"
          >
            {busy ? "Allocating…" : (isNewAttribution ? "Allocate" : "Change partner")}
          </Button>
        </div>
      </div>
    </div>
  );
}


function PricingPanel({ summary }) {
  const mode = summary.pricing_mode || "local_scwt";
  const tone = PRICING_MODE_LABELS[mode] || PRICING_MODE_LABELS.local_scwt;
  const sourceLabel = PRICE_SOURCE_LABELS[summary.price_source] || (summary.price_source || "—");
  // Jun 2026 — all-inclusive bookings render the frozen line-item
  // breakdown. The legacy "Kit + postage allowance" figure comes from
  // the old national-partner table and is stale under all-inclusive
  // (postage is baked into the sticker price), so it is hidden here.
  // Partner margin stays visible — it still caps ledger commissions.
  const isAllInclusive = summary.pricing_model === "all_inclusive_v1";
  const KIT_SOURCE_PRICING_LABELS = {
    aware_mailout:        "AWARE posts kit — postage included",
    lab_collection_return:"Customer collects & returns kit at lab",
    customer_lab_dropoff: "Customer's own bottle — lab drop-off",
    partner_supplied:     "Partner-supplied kit (from partner stock)",
  };
  const breakdown = Array.isArray(summary.pricing_breakdown) ? summary.pricing_breakdown : [];
  return (
    <div
      data-testid="booking-pricing-panel"
      className="mt-4 rounded-xl bg-white border border-slate-200 p-4"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <CreditCard className="w-4 h-4 text-slate-500" />
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-700">Pricing</div>
          <span
            data-testid="booking-pricing-mode-chip"
            className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${tone.bg} ${tone.fg}`}
          >
            {tone.label}
          </span>
          <span
            data-testid="booking-pricing-source-chip"
            className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold"
          >
            {sourceLabel}
          </span>
          {summary.admin_price_override && (
            <span
              data-testid="booking-pricing-override-chip"
              className="text-[10px] px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 font-bold uppercase tracking-wider"
            >
              Override
            </span>
          )}
        </div>
        <div
          data-testid="booking-pricing-final-price"
          className="text-base font-bold text-slate-900"
        >
          ${Number(summary.final_price ?? 0).toFixed(2)}
        </div>
      </div>
      {isAllInclusive ? (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
            {breakdown.map((li) => (
              <Row key={li.code} label={li.label}>
                <div
                  data-testid={`booking-pricing-li-${li.code}`}
                  className={Number(li.amount_cents) < 0 ? "text-emerald-700 font-semibold" : ""}
                >
                  {Number(li.amount_cents) < 0 ? "−" : ""}${Math.abs(Number(li.amount_cents || 0) / 100).toFixed(2)}
                </div>
              </Row>
            ))}
            <Row label="Kit source">
              <div data-testid="booking-pricing-kit-source">
                {KIT_SOURCE_PRICING_LABELS[summary.kit_source] || summary.kit_source || "—"}
              </div>
            </Row>
            {Number(summary.partner_margin_allowance ?? 0) > 0 && (
              <Row label="Partner margin (commission cap)">
                <div data-testid="booking-pricing-partner-margin">
                  ${Number(summary.partner_margin_allowance).toFixed(2)}
                </div>
              </Row>
            )}
          </div>
          {summary.kit_source === "aware_mailout" && (
            <div className="mt-2 text-[11px] text-slate-500" data-testid="booking-pricing-postage-note">
              Postage &amp; prepaid return label are included in the all-inclusive package price — no separate kit fee applies.
            </div>
          )}
          {summary.kit_source === "partner_supplied" && (
            <div className="mt-2 text-[11px] text-emerald-700" data-testid="booking-pricing-reimbursement-note">
              Partner supplied the kit from their own stock — the kit reimbursement (default $29) is recorded automatically in the partner ledger once the booking is paid.
            </div>
          )}
        </>
      ) : (
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
        <Row label="Package price">
          <div data-testid="booking-pricing-base-price">
            ${Number(summary.final_price ?? 0).toFixed(2)}
          </div>
        </Row>
        <Row label="Kit + postage allowance">
          <div data-testid="booking-pricing-kit-postage-allowance">
            {Number(summary.kit_postage_allowance ?? 0) > 0
              ? `$${Number(summary.kit_postage_allowance).toFixed(2)}`
              : "—"}
          </div>
        </Row>
        <Row label="Partner margin allowance">
          <div data-testid="booking-pricing-partner-margin">
            {Number(summary.partner_margin_allowance ?? 0) > 0
              ? `$${Number(summary.partner_margin_allowance).toFixed(2)}`
              : "—"}
          </div>
        </Row>
        <Row label="Local retail baseline">
          <div data-testid="booking-pricing-local-baseline">
            {summary.local_retail_baseline != null
              ? `$${Number(summary.local_retail_baseline).toFixed(2)}`
              : "—"}
          </div>
        </Row>
      </div>
      )}
      <div className="mt-3 text-[11px] text-slate-500 flex items-center gap-3 flex-wrap">
        <span data-testid="booking-pricing-version">
          Price version: <code className="font-mono">{summary.price_version || "—"}</code>
        </span>
        {summary.admin_price_override_reason && (
          <span data-testid="booking-pricing-override-reason">
            Override reason: {summary.admin_price_override_reason}
          </span>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Phase 8 — Mail-Out Sample Kit panel (admin controls + audit log)
// ════════════════════════════════════════════════════════════════════
// Feb 2026 — canonical admin/staff wording. Mirrors
// `frontend/src/lib/kitMethodLabels.ADMIN_KIT_LABELS`. Kept as a local
// constant so the dropdown iterator here (Object.entries) still works.
const KIT_METHOD_LABELS = {
  mail_out:             "Mail-out — lab kit posted to customer",
  local_pickup_dropoff: "Customer pickup / drop-off at Noosaville",
  existing_bottle:      "Customer has kit or own bottle — no dispatch",
  technician_collected: "Technician collects sample during service",
  admin_supplied:       "Kit supplied by admin/lab at counter",
};
const KIT_STATUS_TONES = {
  pending:      { bg: "bg-amber-100",   fg: "text-amber-800",   label: "Pending" },
  packed:       { bg: "bg-sky-100",     fg: "text-sky-800",     label: "Packed" },
  sent:         { bg: "bg-indigo-100",  fg: "text-indigo-800",  label: "Sent" },
  delivered:    { bg: "bg-purple-100",  fg: "text-purple-800",  label: "Delivered" },
  returned:     { bg: "bg-emerald-100", fg: "text-emerald-800", label: "Returned" },
  not_required: { bg: "bg-slate-200",   fg: "text-slate-700",   label: "Not required" },
};

function KitPanel({ summary, onChanged }) {
  const ref = summary.reference;
  const [busy, setBusy] = useState(null);
  const [outboundTracking, setOutboundTracking] = useState(summary.kit_tracking_number || "");
  const [returnTracking, setReturnTracking]     = useState(summary.return_tracking_number || "");
  const status = summary.kit_status || "pending";
  const techCollected = summary.collection_method === "technician_collected";
  // Jun 2026 — technician-collected wording: the customer never returns
  // anything, so "Returned" reads wrong; the same action simply records
  // that the physical sample arrived at the lab.
  const baseTone = KIT_STATUS_TONES[status] || KIT_STATUS_TONES.pending;
  const tone = techCollected && status === "returned"
    ? { ...baseTone, label: "Sample received" }
    : baseTone;

  // Keep the tracking inputs in sync if the parent reloads the booking.
  useEffect(() => {
    setOutboundTracking(summary.kit_tracking_number || "");
    setReturnTracking(summary.return_tracking_number || "");
  }, [summary.kit_tracking_number, summary.return_tracking_number]);

  const call = async (label, doIt) => {
    setBusy(label);
    try {
      await doIt();
      toast.success(`Kit ${label}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e) || `Unable to ${label}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid="booking-kit-panel"
      className="mt-4 rounded-xl bg-white border border-slate-200 p-4"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-slate-500" />
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-700">Sample kit</div>
          <span
            data-testid="booking-kit-status-chip"
            className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${tone.bg} ${tone.fg}`}
          >
            {tone.label}
          </span>
          <span
            data-testid="booking-kit-method-chip"
            className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold"
          >
            {KIT_METHOD_LABELS[summary.kit_method] || summary.kit_method}
          </span>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <Row label="Sent">
          <div data-testid="booking-kit-sent-date" className="text-[13px]">
            {summary.kit_sent_date ? formatDateTimeAU(summary.kit_sent_date) : "—"}
          </div>
        </Row>
        <Row label="Sample received">
          <div data-testid="booking-kit-received-date" className="text-[13px]">
            {summary.sample_received_date ? formatDateTimeAU(summary.sample_received_date) : "—"}
          </div>
        </Row>
        <Row label="Outgoing tracking">
          <div className="flex items-center gap-1.5">
            <input
              type="text"
              value={outboundTracking}
              onChange={(e) => setOutboundTracking(e.target.value)}
              data-testid="booking-kit-tracking-input"
              placeholder="e.g. AP123456789AU"
              className="text-[12px] rounded-md border border-slate-200 px-2 py-1 flex-1 min-w-0"
            />
            <button
              type="button"
              disabled={busy !== null || !outboundTracking.trim()}
              data-testid="booking-kit-save-tracking-btn"
              onClick={() => call("tracking saved", () =>
                api.patch(`/admin/bookings/${ref}/kit/tracking`, { tracking_number: outboundTracking.trim() })
              )}
              className="text-[11px] font-semibold px-2 py-1 rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </Row>
        <Row label="Return tracking">
          <div className="flex items-center gap-1.5">
            <input
              type="text"
              value={returnTracking}
              onChange={(e) => setReturnTracking(e.target.value)}
              data-testid="booking-kit-return-tracking-input"
              placeholder="e.g. AP987654321AU"
              className="text-[12px] rounded-md border border-slate-200 px-2 py-1 flex-1 min-w-0"
            />
            <button
              type="button"
              disabled={busy !== null || !returnTracking.trim()}
              data-testid="booking-kit-save-return-tracking-btn"
              onClick={() => call("return tracking saved", () =>
                api.patch(`/admin/bookings/${ref}/kit/return-tracking`, { tracking_number: returnTracking.trim() })
              )}
              className="text-[11px] font-semibold px-2 py-1 rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </Row>
      </div>

      <div className="flex flex-wrap gap-2 mb-3" data-testid="booking-kit-actions">
        <button
          type="button"
          disabled={busy !== null || status === "packed"}
          data-testid="booking-kit-mark-packed-btn"
          onClick={() => call("marked packed", () =>
            api.post(`/admin/bookings/${ref}/kit/mark-packed`)
          )}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full bg-sky-100 text-sky-800 hover:bg-sky-200 disabled:opacity-50"
        >
          <Package className="w-3 h-3" /> Mark packed
        </button>
        <button
          type="button"
          disabled={busy !== null || status === "sent"}
          data-testid="booking-kit-mark-sent-btn"
          onClick={() => call("marked posted", () =>
            api.post(`/admin/bookings/${ref}/kit/mark-sent`,
              outboundTracking.trim() ? { tracking_number: outboundTracking.trim() } : {})
          )}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full bg-indigo-100 text-indigo-800 hover:bg-indigo-200 disabled:opacity-50"
        >
          <Truck className="w-3 h-3" /> Mark posted
        </button>
        <button
          type="button"
          disabled={busy !== null || status === "returned"}
          data-testid="booking-kit-mark-returned-btn"
          onClick={() => call(techCollected ? "sample received" : "marked returned", () =>
            api.post(`/admin/bookings/${ref}/kit/mark-returned`,
              returnTracking.trim() ? { return_tracking_number: returnTracking.trim() } : {})
          )}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 hover:bg-emerald-200 disabled:opacity-50"
        >
          <Inbox className="w-3 h-3" /> {techCollected ? "Mark sample received" : "Mark returned"}
        </button>
        <button
          type="button"
          disabled={busy !== null || status === "not_required"}
          data-testid="booking-kit-mark-not-required-btn"
          onClick={() => {
            const reason = window.prompt("Reason kit is not required (optional):") || "";
            return call("marked not required", () =>
              api.post(`/admin/bookings/${ref}/kit/mark-not-required`, { reason: reason || null })
            );
          }}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full bg-slate-200 text-slate-700 hover:bg-slate-300 disabled:opacity-50"
        >
          <X className="w-3 h-3" /> Mark not required
        </button>
        <select
          data-testid="booking-kit-method-select"
          value={summary.kit_method || "mail_out"}
          disabled={busy !== null}
          onChange={(e) => {
            const next = e.target.value;
            if (next === summary.kit_method) return;
            return call("method changed", () =>
              api.patch(`/admin/bookings/${ref}/kit/method`, { kit_method: next })
            );
          }}
          className="text-[11px] font-semibold px-2 py-1.5 rounded-full border border-slate-200 bg-white"
        >
          {Object.entries(KIT_METHOD_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {(summary.kit_audit_log || []).length > 0 && (
        <details
          className="text-xs"
          data-testid="booking-kit-audit-log"
        >
          <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700">
            Audit log ({(summary.kit_audit_log || []).length})
          </summary>
          <ul className="mt-2 space-y-1 text-[11px] font-mono text-slate-700">
            {(summary.kit_audit_log || []).slice().reverse().map((e, i) => (
              <li key={i} className="border-l-2 border-slate-200 pl-2">
                <span className="text-slate-500">{e.at ? formatDateTimeAU(e.at) : "—"}</span>
                {" · "}<strong>{e.action}</strong>
                {e.user_email ? <> · {e.user_email}</> : null}
                {e.kit_method ? <> · method=<code>{e.kit_method}</code></> : null}
                {e.kit_tracking_number ? <> · tracking=<code>{e.kit_tracking_number}</code></> : null}
                {e.return_tracking_number ? <> · return=<code>{e.return_tracking_number}</code></> : null}
                {e.reason ? <> · {e.reason}</> : null}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Phase 2L-A144 — Paid Posted Sample Kit fulfilment panel
// ════════════════════════════════════════════════════════════════════
const SHIPPING_PROVIDER_LABELS = {
  manual:         "Manual (Aware staff)",
  australia_post: "Australia Post (eParcel)",
  starshipit:     "StarShipIT",
  other:          "Other",
};
const INTEGRATION_STATUS_LABELS = {
  not_required:         "Not required",
  awaiting_fulfilment:  "Awaiting fulfilment",
  fulfilment_triggered: "Fulfilment triggered",
  labels_generated:     "Labels generated",
  labels_printed:       "Labels printed",
  kit_packed:           "Kit packed",
  kit_posted:           "Kit posted",
  error:                "Error / action required",
};
const LABEL_STATUS_LABELS = {
  pending:    "Pending",
  generated:  "Generated",
  printed:    "Printed",
  reprinted:  "Reprinted",
  failed:     "Failed",
};
const RETURN_LABEL_TYPE_LABELS = {
  preprinted_return_paid: "Preprinted AusPost Return Paid",
  generated_auspost:      "Generated (AusPost)",
  manual_other:           "Manual / Other",
};

function PostedKitAddressEditor({ detail, patch, saving }) {
  // Jun 2026 — lets admins repair mail-out bookings that were stamped
  // without a delivery address (all-inclusive checkout bug). Saving a
  // valid address auto-pulls a "not required" mail-out booking into
  // the fulfilment queue server-side.
  const a = detail.posted_kit_address || {};
  // Nov 2026 (SCWT-34E6D6) — when there's no structured address on
  // file yet, pre-fill the form from the free-text property address
  // the customer already typed at booking time, so staff aren't
  // retyping from scratch. This is only a SUGGESTION — nothing is
  // saved until staff review/confirm and click Save.
  const suggested = useMemo(() => {
    if (a.line1 || !detail.legacy_customer_address) return null;
    const parsed = parseAusAddress(detail.legacy_customer_address);
    if (!parsed.line1 && !parsed.suburb) return null;
    return parsed;
  }, [a.line1, detail.legacy_customer_address]);
  const [draft, setDraft] = useState({
    line1: a.line1 || suggested?.line1 || "",
    line2: a.line2 || suggested?.line2 || "",
    suburb: a.suburb || suggested?.suburb || "",
    state: a.state || suggested?.state || "",
    postcode: a.postcode || suggested?.postcode || "",
  });
  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  const canSave = draft.line1.trim() && draft.suburb.trim() && draft.state.trim() && /^\d{4}$/.test(draft.postcode.trim());
  const save = () => patch({
    posted_kit_address: {
      line1: draft.line1.trim(), line2: draft.line2.trim() || null,
      suburb: draft.suburb.trim(), state: draft.state.trim(),
      postcode: draft.postcode.trim(), country: "Australia",
    },
  });
  const inputCls = "mt-1 w-full h-10 rounded-lg border border-slate-300 bg-white px-2 text-[12px]";
  return (
    <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="admin-posted-kit-address-editor">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        Delivery address {!detail.posted_kit_address && (
          <span className="ml-1 normal-case tracking-normal font-normal text-amber-700">
            — not on file; required before a shipping label can be created
          </span>
        )}
      </div>
      {suggested && (
        <div className="mt-1 text-[11px] text-cyan-700" data-testid="admin-posted-kit-addr-suggestion">
          Suggested from the original booking ("{detail.legacy_customer_address}") — please confirm or correct below before saving.
        </div>
      )}
      <div className="mt-2 grid sm:grid-cols-2 gap-2">
        <input placeholder="Street address" value={draft.line1} onChange={set("line1")} disabled={saving}
               data-testid="admin-posted-kit-addr-line1" className={inputCls} />
        <input placeholder="Line 2 (optional)" value={draft.line2} onChange={set("line2")} disabled={saving}
               data-testid="admin-posted-kit-addr-line2" className={inputCls} />
        <input placeholder="Suburb" value={draft.suburb} onChange={set("suburb")} disabled={saving}
               data-testid="admin-posted-kit-addr-suburb" className={inputCls} />
        <div className="grid grid-cols-2 gap-2">
          <input placeholder="State" value={draft.state} onChange={set("state")} disabled={saving}
                 data-testid="admin-posted-kit-addr-state" className={inputCls} />
          <input placeholder="Postcode" value={draft.postcode} onChange={set("postcode")} disabled={saving}
                 data-testid="admin-posted-kit-addr-postcode" className={inputCls} />
        </div>
      </div>
      <button
        onClick={save}
        disabled={saving || !canSave}
        data-testid="admin-posted-kit-addr-save-btn"
        className="mt-2 px-4 h-9 rounded-full text-[12px] font-semibold bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save delivery address"}
      </button>
    </div>
  );
}

function PostedKitFulfilmentPanel({ reference, onChanged }) {
  const ref = reference;
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/admin/bookings/${ref}/posted-kit`)
      .then(({ data }) => setDetail(data))
      .catch((e) => {
        // 404 = booking gone (unlikely here since we came from
        // /admin/bookings/{ref}); anything else is noise for the panel.
        if (e?.response?.status !== 404) {
          console.warn("posted-kit panel load failed:", e);
        }
        setDetail(null);
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (ref) load(); }, [ref]);

  // Self-hide when the booking has no posted-kit state at all. We
  // treat the booking as "posted-kit-relevant" if ANY of these is true:
  //   * paid_at_creation was true (customer paid through checkout),
  //   * an admin has stamped a shipping_integration_status,
  //   * or the fee_cents was recorded.
  const shouldShow = !!detail && (
    detail.posted_kit_paid_at_creation
    || (detail.shipping_integration_status && detail.shipping_integration_status !== "not_required")
    || detail.posted_kit_fee_cents
    // Jun 2026 — always surface the panel for mail-out bookings so an
    // admin can repair rows that were stamped without a shipping
    // address (all-inclusive checkout bug) and get their label created.
    || detail.kit_method === "mail_out"
  );

  if (loading && !detail) {
    return null;      // load quietly; no flicker on the majority of bookings
  }
  if (!shouldShow) return null;

  const patch = (payload) => {
    setSaving(true);
    return api.patch(`/admin/bookings/${ref}/posted-kit`, payload)
      .then(({ data }) => {
        setDetail(data);
        toast.success("Posted-kit fulfilment updated");
        if (onChanged) onChanged();
      })
      .catch((e) => toast.error(formatApiError(e, "Could not update posted-kit fulfilment")))
      .finally(() => setSaving(false));
  };

  const triggerManual = () => {
    setTriggering(true);
    api.post(`/admin/bookings/${ref}/posted-kit/trigger`, {})
      .then(({ data }) => {
        if (data.fired) toast.success(data.note);
        else toast.info(data.note);
        load();
        if (onChanged) onChanged();
      })
      .catch((e) => toast.error(formatApiError(e, "Could not trigger fulfilment")))
      .finally(() => setTriggering(false));
  };

  const feeAud = detail.posted_kit_fee_cents ? (detail.posted_kit_fee_cents / 100).toFixed(2) : null;
  const statusKey = detail.shipping_integration_status || "not_required";
  const statusColor = {
    not_required:         "bg-slate-100 text-slate-700",
    awaiting_fulfilment:  "bg-amber-100 text-amber-800",
    fulfilment_triggered: "bg-cyan-100 text-cyan-800",
    labels_generated:     "bg-cyan-100 text-cyan-800",
    labels_printed:       "bg-cyan-100 text-cyan-800",
    kit_packed:           "bg-emerald-100 text-emerald-800",
    kit_posted:           "bg-emerald-100 text-emerald-800",
    error:                "bg-rose-100 text-rose-800",
  }[statusKey] || "bg-slate-100 text-slate-700";

  return (
    <div
      className="mt-4 rounded-xl bg-white border border-cyan-200 p-4 space-y-4"
      data-testid="admin-posted-kit-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">
            Posted sample kit fulfilment
          </div>
          <div className="mt-0.5 text-[12px] text-slate-500">
            {detail.posted_kit_paid_at_creation
              ? "Customer paid the posted-kit fee at checkout."
              : "Postage is included in this booking's price — not charged as a separate line item."}
          </div>
        </div>
        <span
          className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${statusColor}`}
          data-testid="admin-posted-kit-status-pill"
        >
          {INTEGRATION_STATUS_LABELS[statusKey] || statusKey}
        </span>
      </div>

      {/* Read-only summary */}
      <div className="grid sm:grid-cols-2 gap-3 text-[12px]">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Fee paid</div>
          <div className="font-mono text-slate-900">{feeAud ? `$${feeAud} AUD` : "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Provider</div>
          <div className="text-slate-900">{SHIPPING_PROVIDER_LABELS[detail.shipping_provider] || detail.shipping_provider || "—"}</div>
        </div>
        <div className="sm:col-span-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Delivery address</div>
          <div className="text-slate-900 whitespace-pre-line" data-testid="admin-posted-kit-address">
            {detail.posted_kit_address
              ? [
                  detail.posted_kit_address.line1,
                  detail.posted_kit_address.line2,
                  `${detail.posted_kit_address.suburb || ""} ${detail.posted_kit_address.state || ""} ${detail.posted_kit_address.postcode || ""}`.trim(),
                  detail.posted_kit_address.country,
                ].filter(Boolean).join("\n")
              : <span className="text-slate-400">Not on file</span>}
          </div>
          {detail.posted_kit_delivery_notes && (
            <div className="mt-1 text-[11px] text-slate-600 italic">Notes: {detail.posted_kit_delivery_notes}</div>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Fulfilment triggered</div>
          <div className="text-slate-900 font-mono text-[11px]" data-testid="admin-posted-kit-fulfilment-triggered-at">
            {detail.fulfilment_triggered_at || <span className="text-slate-400">—</span>}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Label print</div>
          <div className="text-slate-900">{LABEL_STATUS_LABELS[detail.label_print_status] || detail.label_print_status || "—"}</div>
        </div>
      </div>

      {detail.fulfilment_error_message && (
        <div
          className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-[12px] text-rose-800"
          data-testid="admin-posted-kit-error-banner"
        >
          <strong>Fulfilment error:</strong> {detail.fulfilment_error_message}
        </div>
      )}

      {/* Editable admin controls — auto-expanded when there's no
          delivery address on file yet, so staff land straight on the
          fix instead of having to know to expand this first. */}
      <details className="mt-2 pt-3 border-t border-slate-200" data-testid="admin-posted-kit-editor" open={!detail.posted_kit_address || undefined}>
        <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700">
          Edit fulfilment fields
        </summary>
        <PostedKitAddressEditor detail={detail} patch={patch} saving={saving} />
        <div className="mt-3 grid sm:grid-cols-2 gap-3 text-[12px]">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Shipping provider</span>
            <select
              value={detail.shipping_provider || "manual"}
              onChange={(e) => patch({ shipping_provider: e.target.value })}
              disabled={saving}
              data-testid="admin-posted-kit-provider-select"
              className="mt-1 w-full h-10 rounded-lg border border-slate-300 bg-white px-2"
            >
              {Object.entries(SHIPPING_PROVIDER_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Integration status</span>
            <select
              value={detail.shipping_integration_status || "awaiting_fulfilment"}
              onChange={(e) => patch({ shipping_integration_status: e.target.value })}
              disabled={saving}
              data-testid="admin-posted-kit-status-select"
              className="mt-1 w-full h-10 rounded-lg border border-slate-300 bg-white px-2"
            >
              {Object.entries(INTEGRATION_STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Outbound tracking #</span>
            <input
              defaultValue={detail.outbound_tracking_number || ""}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== (detail.outbound_tracking_number || "")) patch({ outbound_tracking_number: v });
              }}
              disabled={saving}
              data-testid="admin-posted-kit-outbound-tracking"
              className="mt-1 w-full h-10 rounded-lg border border-slate-300 bg-white px-2 font-mono text-[12px]"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Return tracking #</span>
            <input
              defaultValue={detail.return_tracking_number_ext || ""}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== (detail.return_tracking_number_ext || "")) patch({ return_tracking_number_ext: v });
              }}
              disabled={saving}
              data-testid="admin-posted-kit-return-tracking"
              className="mt-1 w-full h-10 rounded-lg border border-slate-300 bg-white px-2 font-mono text-[12px]"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Return label type</span>
            <select
              value={detail.return_label_type || ""}
              onChange={(e) => e.target.value && patch({ return_label_type: e.target.value })}
              disabled={saving}
              data-testid="admin-posted-kit-return-label-type"
              className="mt-1 w-full h-10 rounded-lg border border-slate-300 bg-white px-2"
            >
              <option value="">— select —</option>
              {Object.entries(RETURN_LABEL_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Label print status</span>
            <select
              value={detail.label_print_status || ""}
              onChange={(e) => e.target.value && patch({ label_print_status: e.target.value })}
              disabled={saving}
              data-testid="admin-posted-kit-label-print-status"
              className="mt-1 w-full h-10 rounded-lg border border-slate-300 bg-white px-2"
            >
              <option value="">— select —</option>
              {Object.entries(LABEL_STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Date kit posted</span>
            <input
              type="date"
              defaultValue={(detail.date_kit_posted || "").slice(0, 10)}
              onBlur={(e) => {
                const v = e.target.value;
                if (v && v !== (detail.date_kit_posted || "").slice(0, 10)) patch({ date_kit_posted: v });
              }}
              disabled={saving}
              data-testid="admin-posted-kit-date-posted"
              className="mt-1 w-full h-10 rounded-lg border border-slate-300 bg-white px-2"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Date sample received</span>
            <input
              type="date"
              defaultValue={(detail.date_sample_received || "").slice(0, 10)}
              onBlur={(e) => {
                const v = e.target.value;
                if (v && v !== (detail.date_sample_received || "").slice(0, 10)) patch({ date_sample_received: v });
              }}
              disabled={saving}
              data-testid="admin-posted-kit-date-received"
              className="mt-1 w-full h-10 rounded-lg border border-slate-300 bg-white px-2"
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Kit packing notes</span>
            <textarea
              defaultValue={detail.kit_packing_notes || ""}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v !== (detail.kit_packing_notes || "")) patch({ kit_packing_notes: v || null });
              }}
              disabled={saving}
              rows={2}
              data-testid="admin-posted-kit-packing-notes"
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-2 resize-none"
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Sample condition notes</span>
            <textarea
              defaultValue={detail.sample_condition_notes || ""}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v !== (detail.sample_condition_notes || "")) patch({ sample_condition_notes: v || null });
              }}
              disabled={saving}
              rows={2}
              data-testid="admin-posted-kit-sample-condition-notes"
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-2 resize-none"
            />
          </label>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={triggerManual}
            disabled={triggering || saving}
            data-testid="admin-posted-kit-trigger-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-[12px] font-semibold rounded-md bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-40"
          >
            {triggering ? "Triggering…" : "Trigger fulfilment event"}
          </button>
          <button
            type="button"
            onClick={() => patch({ shipping_provider: "manual", fulfilment_error_message: null })}
            disabled={saving}
            data-testid="admin-posted-kit-manual-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-[12px] font-semibold rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            Switch to manual handling
          </button>
        </div>
      </details>

      {/* Audit log */}
      {(detail.posted_kit_audit_log || []).length > 0 && (
        <details className="mt-2 pt-3 border-t border-slate-200" data-testid="admin-posted-kit-audit">
          <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700">
            Audit log ({(detail.posted_kit_audit_log || []).length})
          </summary>
          <ul className="mt-2 space-y-1 text-[11px] font-mono text-slate-700">
            {(detail.posted_kit_audit_log || []).slice().reverse().map((e, i) => (
              <li key={i} className="border-l-2 border-slate-200 pl-2">
                <span className="text-slate-500">{e.at ? formatDateTimeAU(e.at) : "—"}</span>
                {" · "}<strong>{e.action}</strong>
                {e.actor ? <> · {e.actor}</> : null}
                {e.fields ? <> · {e.fields.join(", ")}</> : null}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════
// Phase 2L-A123 — Admin Mark PM Invoice as Paid dialog
// ════════════════════════════════════════════════════════════════════
function MarkPaidDialog({ open, onClose, reference, defaultAmount, onMarkedPaid }) {
  const todayLocal = () => {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString().slice(0, 10);
  };
  const [paidDate, setPaidDate] = useState(todayLocal());
  const [amount, setAmount] = useState(defaultAmount || 0);
  const [method, setMethod] = useState("bank_transfer");
  const [paymentRef, setPaymentRef] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setPaidDate(todayLocal());
      setAmount(defaultAmount || 0);
      setMethod("bank_transfer");
      setPaymentRef("");
      setNotes("");
      setSubmitting(false);
    }
  }, [open, defaultAmount]);

  const refRecommended = (method === "bank_transfer" || method === "eft");
  const refMissingWarning = refRecommended && !paymentRef.trim();

  const submit = async () => {
    if (!(amount > 0)) {
      toast.error("Amount paid must be greater than $0.");
      return;
    }
    const paidIso = new Date(paidDate + "T00:00:00").toISOString();
    if (new Date(paidIso).getTime() > Date.now() + 60_000) {
      toast.error("Paid date cannot be in the future.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(
        `/admin/bookings/${reference}/mark-paid-manual`,
        {
          paid_at: paidIso,
          amount_paid: Number(amount),
          payment_method: method,
          payment_reference: paymentRef.trim() || null,
          notes: notes.trim() || null,
        },
      );
      toast.success(
        `${data.reference} marked paid · ${method.replace("_", " ")} · $${Number(amount).toFixed(2)}`
      );
      onMarkedPaid?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose?.()}>
      <DialogContent
        data-testid="admin-mark-paid-dialog"
        className="max-w-md"
      >
        <DialogHeader>
          <DialogTitle>Mark payment received</DialogTitle>
          <DialogDescription>
            Record the bank transfer / EFT / cheque payment against{" "}
            <span className="font-mono font-semibold">{reference}</span>.
            This flips the booking to <strong>paid</strong> and is
            permanent.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-700">Paid date</label>
            <input
              type="date"
              data-testid="admin-mark-paid-date"
              value={paidDate}
              max={todayLocal()}
              onChange={(e) => setPaidDate(e.target.value)}
              className="mt-1.5 w-full h-11 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-700">
              Amount paid <span className="text-rose-600">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              data-testid="admin-mark-paid-amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1.5 w-full h-11 rounded-xl border border-slate-200 px-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-400"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-700">
              Payment method <span className="text-rose-600">*</span>
            </label>
            <select
              data-testid="admin-mark-paid-method"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="mt-1.5 w-full h-11 rounded-xl border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
            >
              <option value="bank_transfer">Bank transfer</option>
              <option value="eft">EFT</option>
              <option value="cheque">Cheque</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-700">
              Payment reference{" "}
              <span className="text-slate-400 normal-case lowercase tracking-normal">
                {refRecommended ? "(recommended)" : "(optional)"}
              </span>
            </label>
            <input
              type="text"
              data-testid="admin-mark-paid-reference"
              value={paymentRef}
              onChange={(e) => setPaymentRef(e.target.value)}
              placeholder="e.g. TXN-7788 · CBA 23/06/2026"
              maxLength={200}
              className={`mt-1.5 w-full h-11 rounded-xl border px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-400 ${refMissingWarning ? "border-amber-300 bg-amber-50" : "border-slate-200"}`}
            />
            {refMissingWarning && (
              <div className="mt-1 text-[11px] text-amber-700 flex items-center gap-1.5" data-testid="admin-mark-paid-reference-warning">
                <AlertTriangle className="w-3 h-3" />
                A reference is recommended for {method.replace("_", " ")} payments so the agency can reconcile later.
              </div>
            )}
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-700">
              Notes <span className="text-slate-400 normal-case lowercase tracking-normal">(optional)</span>
            </label>
            <textarea
              rows={2}
              data-testid="admin-mark-paid-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={2000}
              placeholder="Internal note for the timeline (e.g. 'Coastal Realty Noosa trust account')"
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-y"
            />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            data-testid="admin-mark-paid-cancel"
            onClick={onClose}
            disabled={submitting}
            className="h-11 px-4 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="admin-mark-paid-submit"
            onClick={submit}
            disabled={submitting || !(amount > 0)}
            className="h-11 px-5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold disabled:opacity-50 inline-flex items-center gap-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {submitting ? "Recording…" : "Mark paid"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ════════════════════════════════════════════════════════════════════
// Phase 2L-A124 follow-up — Admin Re-send PM Invoice Email dialog
// ════════════════════════════════════════════════════════════════════
function RemovePaidStatusDialog({ open, onClose, summary, onRemoved }) {
  const [reason, setReason] = useState("");
  const [confirmStripe, setConfirmStripe] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const likelyStripe = !summary.manual_payment && !!summary.payment_session_id;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${summary.reference}/remove-paid-status`, {
        reason, ...(confirmStripe ? { confirm_stripe: true } : {}),
      });
      toast.success(`Paid status removed (${data.paid_source} payment)${data.pay_link_available ? " — pay link can now be resent" : ""}`);
      onClose(); onRemoved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to remove paid status");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="remove-paid-status-dialog">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
        <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-rose-600" /> Remove paid status</h3>
        <p className="text-sm text-slate-600 mt-2">
          This will remove the paid status from booking <b>{summary.reference}</b> and allow a payment link or invoice
          to be resent. It will <b>not</b> change the published report, results, or sample status. Continue?
        </p>
        {likelyStripe && (
          <label className="mt-3 flex items-start gap-2 rounded-lg bg-rose-50 border border-rose-200 p-3 text-xs text-rose-800">
            <input type="checkbox" data-testid="remove-paid-confirm-stripe" className="mt-0.5" checked={confirmStripe} onChange={(e) => setConfirmStripe(e.target.checked)} />
            <span>This booking appears to have a <b>confirmed Stripe transaction</b>. Removing the paid status will NOT
            refund or alter the Stripe payment — the transaction record is preserved. I understand and want to proceed.</span>
          </label>
        )}
        <div className="mt-3">
          <Label className="text-xs font-bold text-slate-600">Reason for removing paid status</Label>
          <Textarea data-testid="remove-paid-reason" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. marked paid by mistake / customer payment not received / wrong booking marked paid"
            className="mt-1 text-sm" rows={2} />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button data-testid="remove-paid-cancel" variant="outline" className="rounded-full" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button data-testid="remove-paid-submit" className="rounded-full bg-rose-600 hover:bg-rose-700"
            disabled={busy || reason.trim().length < 3} onClick={submit}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Remove paid status"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ResendConfirmationInline({ reference, lastResentAt }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${reference}/resend-confirmation`);
      setDone(true);
      toast.success(`Confirmation email resent to ${data.sent_to}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to resend confirmation");
    } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      data-testid="admin-resend-confirmation-btn"
      onClick={send}
      disabled={busy}
      title={lastResentAt ? `Last resent ${formatDateTimeAU(lastResentAt)}` : "Resend the booking confirmation email (with label PDF where applicable)"}
      className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-full px-3 py-1.5 shadow-sm transition-colors disabled:opacity-60"
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
      {done ? "Confirmation resent ✓" : "Resend confirmation"}
    </button>
  );
}

function AdjustAmountInline({ reference, currentTotal }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const val = Number(amount);
    if (!Number.isFinite(val) || val < 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${reference}/update-amount`, { amount: val });
      const linkStatus = data.pay_link?.customer_email_status;
      toast.success(
        `Amount corrected $${Number(data.old_total).toFixed(2)} → $${Number(data.new_total).toFixed(2)}` +
        (linkStatus === "sent" ? " · fresh pay link emailed" : "")
      );
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to update amount");
      setBusy(false);
    }
  };
  if (!open) {
    return (
      <button
        type="button"
        data-testid="admin-adjust-amount-btn"
        onClick={() => { setAmount(String(currentTotal ?? "")); setOpen(true); }}
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-700 bg-white hover:bg-amber-50 border border-amber-300 rounded-full px-3 py-1.5 shadow-sm transition-colors"
      >
        <RefreshCw className="w-3.5 h-3.5" /> Adjust amount
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-[11px] font-semibold text-slate-500">$</span>
      <input
        data-testid="admin-adjust-amount-input"
        type="number" min="0" step="0.01" value={amount}
        onChange={(e) => setAmount(e.target.value)}
        className="w-24 text-[12px] font-semibold border border-amber-300 rounded-full px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
      />
      <button
        type="button"
        data-testid="admin-adjust-amount-confirm-btn"
        onClick={submit} disabled={busy}
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-full px-3 py-1.5 shadow-sm disabled:opacity-60"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        Save &amp; resend link
      </button>
      <button
        type="button"
        data-testid="admin-adjust-amount-cancel-btn"
        onClick={() => setOpen(false)} disabled={busy}
        className="text-[11px] font-semibold text-slate-500 hover:text-slate-700 px-1"
      >
        Cancel
      </button>
    </span>
  );
}

function ResendPayLinkInline({ reference, lastSentAt }) {
  const [busy, setBusy] = useState(false);
  const [sentInfo, setSentInfo] = useState(null);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${reference}/send-pay-link`);
      setSentInfo(data);
      if (data.customer_email_status === "sent") toast.success("Pay link emailed to customer");
      else toast.warning(`Pay link created — customer email ${data.customer_email_status}. Link: ${data.pay_link_url}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send pay link");
    } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      data-testid="admin-resend-pay-link-btn"
      onClick={send}
      disabled={busy}
      title={lastSentAt ? `Last sent ${formatDateTimeAU(lastSentAt)}` : "Send Stripe pay link to customer"}
      className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white bg-cyan-600 hover:bg-cyan-700 rounded-full px-3 py-1.5 shadow-sm transition-colors disabled:opacity-60"
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
      {sentInfo ? "Resent ✓" : lastSentAt ? "Resend pay link" : "Send pay link"}
    </button>
  );
}

function ResendInvoiceDialog({ open, onClose, reference, recipient, onResent }) {
  const [submitting, setSubmitting] = useState(false);
  const [sendTo, setSendTo] = useState(recipient || "");

  useEffect(() => {
    if (open) { setSubmitting(false); setSendTo(recipient || ""); }
  }, [open, recipient]);

  const overridden = sendTo.trim().toLowerCase() !== (recipient || "").trim().toLowerCase();
  const validEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(sendTo.trim());

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(
        `/admin/bookings/${reference}/resend-invoice-email`,
        overridden && sendTo.trim() ? { recipient: sendTo.trim() } : {},
      );
      toast.success(
        `Invoice email re-sent to ${data.recipient}${data.recipient_overridden ? " (one-off override)" : ""}`
      );
      onResent?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(formatApiError(detail) || e.message || "Re-send failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose?.()}>
      <DialogContent
        data-testid="admin-resend-invoice-dialog"
        className="max-w-md"
      >
        <DialogHeader>
          <DialogTitle>Re-send invoice email</DialogTitle>
          <DialogDescription>
            We will re-send the invoice email for{" "}
            <span className="font-mono font-semibold">{reference}</span> to
            the contact below.{" "}
            <strong>Nothing else changes</strong> — no new booking, no
            payment-status change, the PDF download link stays the same.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-3 rounded-xl bg-cyan-50 border border-cyan-200 p-4 text-sm">
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-cyan-700 mb-1">
            Send to
          </div>
          <input
            type="email"
            data-testid="admin-resend-invoice-recipient-input"
            value={sendTo}
            onChange={(e) => setSendTo(e.target.value)}
            placeholder={recipient || "email@example.com"}
            className="w-full rounded-lg border border-cyan-300 bg-white px-3 py-2 font-mono text-sm text-slate-900"
          />
          {overridden && sendTo.trim() ? (
            <div data-testid="admin-resend-invoice-override-note" className="mt-2 text-xs font-semibold text-amber-700">
              One-off send to a different address — the booking's contact email is not changed.
            </div>
          ) : (
            <div className="mt-2 text-xs text-slate-500">
              Default contact for this booking. Edit for a one-off send to a different address.
            </div>
          )}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            data-testid="admin-resend-invoice-cancel"
            onClick={onClose}
            disabled={submitting}
            className="h-11 px-4 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="admin-resend-invoice-submit"
            onClick={submit}
            disabled={submitting || !validEmail}
            className="h-11 px-5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-bold disabled:opacity-50 inline-flex items-center gap-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {submitting ? "Sending…" : "Re-send invoice email"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatusPill({ label, tone = "default", testId }) {
  const tones = {
    default: "bg-slate-100 text-slate-700",
    success: "bg-emerald-100 text-emerald-700",
    warn: "bg-amber-100 text-amber-700",
    danger: "bg-rose-100 text-rose-700",
    // Phase 2L-A65 — used by the comp / no-payment-required pill so the
    // header label is visually distinct from the green Paid pill.
    info: "bg-cyan-100 text-cyan-700",
  };
  return <span data-testid={testId} className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full font-semibold capitalize ${tones[tone]}`}>{label}</span>;
}

function Event({ event, isAdmin }) {
  const Icon = ICONS[event.icon] || Calendar;
  const tone = KIND_TONES[event.kind] || "bg-white text-slate-700 border-slate-200";
  return (
    <li className="relative pl-12">
      <span className={`absolute left-0 top-0 w-9 h-9 rounded-full border-2 ${tone} flex items-center justify-center`}>
        <Icon className="w-4 h-4" />
      </span>
      <div className="rounded-xl bg-slate-50/60 border border-slate-100 p-3">
        <div className="flex items-baseline justify-between gap-2">
          <div className="font-semibold text-sm text-slate-900">{event.title}</div>
          <div className="text-[11px] text-slate-500 whitespace-nowrap font-mono">{formatDateTimeAU(event.ts)}</div>
        </div>
        {event.detail && <p className="text-xs text-slate-600 mt-1 leading-relaxed">{event.detail}</p>}
        {isAdmin && event.actor && (
          <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-slate-400 mt-1.5">
            {event.actor_role || "actor"} · {event.actor}
          </div>
        )}
      </div>
    </li>
  );
}

function QuickActions({ actions, reference, isAdmin }) {
  const items = [
    { key: "view_label", label: "View label PDF", to: `/api/bookings/${reference}/labels.pdf`, external: true },
    { key: "mark_received", label: "Mark sample received", to: `/admin/lab?status=pending_sample&ref=${reference}` },
    { key: "enter_results", label: "Enter results", to: `/admin/lab?ref=${reference}` },
    { key: "submit_for_review", label: "Submit for review", to: `/admin/lab?ref=${reference}` },
    { key: "open_photos", label: "Open photos", to: `/admin/lab?ref=${reference}&tab=photos` },
    { key: "approve_publish", label: "Approve & publish", to: `/admin/lab?status=awaiting_review&ref=${reference}`, admin: true },
    { key: "send_pay_link", label: "Send pay link", to: `/admin/bookings?ref=${reference}`, admin: true },
    { key: "create_proposal", label: "Create PWS estimate", to: `/admin/proposals/new?ref=${reference}`, admin: true },
    { key: "view_proposals", label: "View proposals", to: `/admin/proposals?ref=${reference}`, admin: true },
    { key: "archive", label: "Archive booking", to: `/admin/bookings?archive=${reference}`, admin: true },
    { key: "override_status", label: "Override status", to: `/admin/bookings?ref=${reference}`, admin: true },
    { key: "send_result_email", label: "Send result email", to: `/admin/lab?ref=${reference}&email=true`, admin: true },
  ];
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-2">Quick actions</div>
      <ul className="space-y-1">
        {items
          .filter((i) => actions.includes(i.key) && (!i.admin || isAdmin))
          .map((i) => (
            i.external ? (
              <li key={i.key}>
                <a href={i.to} target="_blank" rel="noopener noreferrer" data-testid={`action-${i.key}`} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-slate-50 text-sm text-slate-800">
                  {i.label}
                  <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                </a>
              </li>
            ) : (
              <li key={i.key}>
                <Link to={i.to} data-testid={`action-${i.key}`} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-slate-50 text-sm text-slate-800">
                  {i.label}
                  <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                </Link>
              </li>
            )
          ))}
      </ul>
    </div>
  );
}


const MAX_NOTE_LEN = 2000;

function InternalNoteComposer({ reference, onAdded }) {
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null); // {tone, msg}
  const trimmed = body.trim();
  const tooLong = body.length > MAX_NOTE_LEN;
  const disabled = submitting || !trimmed || tooLong;

  async function handleSubmit() {
    setFeedback(null);
    setSubmitting(true);
    try {
      await api.post(`/admin/bookings/${reference}/internal-notes`, { body: trimmed });
      setBody("");
      setFeedback({ tone: "success", msg: "Internal note saved." });
      onAdded?.();
    } catch (e) {
      setFeedback({ tone: "error", msg: e?.response?.data?.detail || "Failed to save note." });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="internal-note-composer">
      <div className="flex items-center gap-2 mb-2">
        <StickyNote className="w-3.5 h-3.5 text-cyan-600" />
        <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">Internal note</div>
      </div>
      <p className="text-[11px] text-slate-500 mb-2 leading-relaxed">Visible to staff only. Never sent to customers or appended to PDFs.</p>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Add a quick note for the team…"
        rows={3}
        maxLength={MAX_NOTE_LEN + 100}
        data-testid="internal-note-textarea"
        className="w-full text-xs px-3 py-2 rounded-lg border border-slate-200 focus:border-cyan-500 focus:outline-none resize-y min-h-[72px]"
      />
      <div className="flex items-center justify-between mt-2">
        <div className={`text-[10px] font-mono ${tooLong ? "text-rose-600 font-semibold" : "text-slate-400"}`} data-testid="internal-note-char-count">{body.length} / {MAX_NOTE_LEN}</div>
        <button
          onClick={handleSubmit}
          disabled={disabled}
          data-testid="internal-note-submit"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${disabled ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-cyan-600 text-white hover:bg-cyan-700"}`}
        >
          {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
          {submitting ? "Saving…" : "Add note"}
        </button>
      </div>
      {feedback ? (
        <div data-testid="internal-note-feedback" className={`text-[11px] mt-2 ${feedback.tone === "success" ? "text-emerald-700" : "text-rose-700"}`}>{feedback.msg}</div>
      ) : null}
    </div>
  );
}



/* ───────────────────────────────────────────────────────────────────
   Phase 2L-A32 — Booking Email Client (Stage 1)
   Inline history panel + Compose-modal that POSTs to
   /api/admin/bookings/{ref}/email and refreshes timeline.
   ─────────────────────────────────────────────────────────────────── */

function MessagesSection({ reference, customer, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/bookings/${reference}/messages`);
      setItems(data.items || []);
    } catch (e) {
      // soft-fail — show empty state with a retry button
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [reference]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <section className="mt-6" data-testid="booking-messages-section">
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-cyan-700" />
            <h2 className="font-display text-lg font-semibold text-slate-900">Customer messages</h2>
            <span data-testid="messages-count" className="text-[11px] font-mono text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
              {items.length}
            </span>
          </div>
          <button
            onClick={() => setComposerOpen(true)}
            data-testid="open-email-composer"
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-cyan-600 text-white hover:bg-cyan-700 transition-colors"
          >
            <Send className="w-3 h-3" /> Compose email
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-xs py-6 justify-center" data-testid="messages-loading">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading messages…
          </div>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-6 text-center" data-testid="messages-empty">
            No emails sent yet. Click <strong className="font-semibold text-slate-600">Compose email</strong> to send the first one.
          </div>
        ) : (
          <ol className="space-y-2" data-testid="messages-list">
            {items.map((m) => (
              <MessageRow
                key={m.id}
                message={m}
                expanded={expanded.has(m.id)}
                onToggle={() => toggle(m.id)}
              />
            ))}
          </ol>
        )}
      </div>

      <EmailComposerModal
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        reference={reference}
        customer={customer}
        onSent={() => { load(); onChange?.(); }}
      />
    </section>
  );
}

function MessageRow({ message, expanded, onToggle }) {
  const m = message;
  const isIn = m.direction === "incoming";
  const statusTone =
    m.status === "sent" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : m.status === "failed_or_skipped" ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <li
      className="rounded-xl border border-slate-200 hover:border-slate-300 transition-colors"
      data-testid={`message-row-${m.id}`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left flex items-start gap-3 px-3 py-2.5"
        data-testid={`message-toggle-${m.id}`}
      >
        <div className="mt-0.5">
          {expanded
            ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
            : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">
              {isIn ? "Reply from" : "Sent to"}
            </span>
            <span className="text-xs font-mono text-slate-700 truncate">{isIn ? m.from_email : m.to_email}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${statusTone}`} data-testid={`message-status-${m.id}`}>
              {m.status}
            </span>
            {m.visibility === "admin_only" && (
              <span
                data-testid={`message-visibility-admin-only-${m.id}`}
                title="Hidden from lab technicians"
                className="text-[10px] px-2 py-0.5 rounded-full font-semibold border bg-rose-50 text-rose-700 border-rose-200 inline-flex items-center gap-1"
              >
                <Lock className="w-3 h-3" />
                Admin only
              </span>
            )}
            {m.visibility === "lab_visible" && (
              <span
                data-testid={`message-visibility-lab-visible-${m.id}`}
                title="Visible to lab technicians"
                className="text-[10px] px-2 py-0.5 rounded-full font-semibold border bg-slate-50 text-slate-600 border-slate-200"
              >
                Lab visible
              </span>
            )}
            <span className="ml-auto text-[11px] text-slate-500 font-mono whitespace-nowrap">
              {formatDateTimeAU(m.created_at)}
            </span>
          </div>
          <div className="text-sm font-semibold text-slate-900 mt-0.5 truncate">{m.subject || "(no subject)"}</div>
          {!expanded && (
            <div className="text-[11px] text-slate-500 mt-0.5">
              {m.created_by_name ? `By ${m.created_by_name}` : null}
              {m.cc?.length ? ` · CC ${m.cc.length}` : ""}
              {m.bcc?.length ? ` · BCC ${m.bcc.length}` : ""}
            </div>
          )}
        </div>
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-100" data-testid={`message-body-${m.id}`}>
          {m.cc?.length ? <div className="text-[11px] text-slate-500"><strong>CC:</strong> {m.cc.join(", ")}</div> : null}
          {m.bcc?.length ? <div className="text-[11px] text-slate-500"><strong>BCC:</strong> {m.bcc.join(", ")}</div> : null}
          {m.reply_to ? <div className="text-[11px] text-slate-500"><strong>Reply-To:</strong> {m.reply_to}</div> : null}
          <div
            className="prose prose-sm max-w-none mt-2 text-slate-800 text-sm leading-relaxed [&_hr]:my-3 [&_a]:text-cyan-700"
            // Phase 2L-security-M3 (Feb-2026): defence-in-depth — body
            // is admin-authored and sanitised server-side, but we run
            // DOMPurify on the client too so a future bypass anywhere
            // upstream still can't smuggle <script> into the admin UI.
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.body_html || "") }}
          />
          {m.created_by_name && (
            <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-slate-400 mt-3">
              Sent by {m.created_by_name}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

const MAX_BODY = 50_000;

function EmailComposerModal({ open, onClose, reference, customer, onSent }) {
  const customerEmail = customer?.email || "";
  const customerName = customer?.name || "";
  const greeting = customerName
    ? `Hi ${customerName.split(" ")[0]},\n\n`
    : "Hi there,\n\n";

  const [to, setTo] = useState(customerEmail);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState(greeting);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // Re-seed defaults whenever the modal opens (in case admin opens it
  // multiple times for the same booking after edits).
  useEffect(() => {
    if (open) {
      setTo(customerEmail);
      setSubject("");
      setBody(greeting);
      setCc("");
      setBcc("");
      setShowCcBcc(false);
      setError(null);
    }
  }, [open, customerEmail]);

  const parseList = (s) =>
    s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);

  const validEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const ccList = parseList(cc);
  const bccList = parseList(bcc);
  const invalidCc = ccList.filter((e) => !validEmail(e));
  const invalidBcc = bccList.filter((e) => !validEmail(e));
  const toValid = validEmail(to);
  const canSend = !sending && toValid && subject.trim() && body.trim()
    && invalidCc.length === 0 && invalidBcc.length === 0;

  // Plain-text → simple HTML conversion (preserve paragraphs + line breaks).
  // Admins compose in plain text; we wrap in basic HTML for Resend.
  const bodyToHtml = (txt) => {
    const escaped = txt
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const paragraphs = escaped.split(/\n{2,}/).map(
      (p) => `<p style="margin:0 0 12px;line-height:1.55">${p.replace(/\n/g, "<br>")}</p>`,
    );
    return `<div style="font-family:-apple-system,Segoe UI,Roboto,sans-serif;font-size:14px;color:#0f172a">${paragraphs.join("")}</div>`;
  };

  async function handleSend() {
    if (!canSend) return;
    setError(null);
    setSending(true);
    try {
      const payload = {
        subject: subject.trim(),
        body_html: bodyToHtml(body.trim()),
      };
      if (to !== customerEmail) payload.to_override = to.trim();
      if (ccList.length) payload.cc = ccList;
      if (bccList.length) payload.bcc = bccList;
      const { data } = await api.post(`/admin/bookings/${reference}/email`, payload);
      if (data?.status === "sent") {
        toast.success(`Email sent to ${data.to}`);
      } else {
        toast.warning(`Logged but provider did not confirm (${data?.status || "unknown"})`);
      }
      onSent?.();
      onClose();
    } catch (e) {
      const msg = formatApiError(e?.response?.data?.detail) || e.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="email-composer-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-4 h-4 text-cyan-700" />
            Email customer · <span className="font-mono text-sm text-slate-500">{reference}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Sent from Aware with the standard signature appended. Logged to the booking timeline.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {/* To */}
          <Field label="To">
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              data-testid="compose-to-input"
              className={`w-full text-sm px-3 py-2 rounded-lg border focus:outline-none ${toValid ? "border-slate-200 focus:border-cyan-500" : "border-rose-300 focus:border-rose-500"}`}
            />
            {!toValid && to.length > 0 && (
              <div className="text-[11px] text-rose-600 mt-1">Enter a valid email address.</div>
            )}
          </Field>

          {/* CC / BCC toggle */}
          {!showCcBcc ? (
            <button
              type="button"
              onClick={() => setShowCcBcc(true)}
              data-testid="compose-toggle-cc-bcc"
              className="text-[11px] font-semibold text-cyan-700 hover:text-cyan-900"
            >+ Add CC / BCC</button>
          ) : (
            <div className="space-y-2" data-testid="compose-cc-bcc-fields">
              <Field label="CC">
                <input
                  type="text"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  placeholder="comma-separated"
                  data-testid="compose-cc-input"
                  className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 focus:border-cyan-500 focus:outline-none"
                />
                {invalidCc.length > 0 && (
                  <div className="text-[11px] text-rose-600 mt-1">Invalid: {invalidCc.join(", ")}</div>
                )}
              </Field>
              <Field label="BCC">
                <input
                  type="text"
                  value={bcc}
                  onChange={(e) => setBcc(e.target.value)}
                  placeholder="comma-separated"
                  data-testid="compose-bcc-input"
                  className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 focus:border-cyan-500 focus:outline-none"
                />
                {invalidBcc.length > 0 && (
                  <div className="text-[11px] text-rose-600 mt-1">Invalid: {invalidBcc.join(", ")}</div>
                )}
              </Field>
            </div>
          )}

          {/* Subject */}
          <Field label="Subject">
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={290}
              placeholder="e.g. Your water test results"
              data-testid="compose-subject-input"
              className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 focus:border-cyan-500 focus:outline-none"
            />
            <div className="text-[10px] text-slate-400 mt-1">
              We auto-prefix the subject with <code className="font-mono text-slate-600">[{reference}]</code> so customer replies can be matched back here later.
            </div>
          </Field>

          {/* Body */}
          <Field label="Message">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={9}
              maxLength={MAX_BODY}
              data-testid="compose-body-textarea"
              className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 focus:border-cyan-500 focus:outline-none resize-y min-h-[180px] font-mono"
              placeholder="Type your message…"
            />
            <div className="flex items-center justify-between mt-1">
              <div className="text-[11px] text-slate-500 italic" data-testid="compose-signature-note">
                Standard Aware signature is appended automatically.
              </div>
              <div className="text-[10px] font-mono text-slate-400">{body.length} / {MAX_BODY}</div>
            </div>
          </Field>

          {error && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 p-2.5 text-xs text-rose-700" data-testid="compose-error">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              data-testid="compose-cancel"
              className="px-3 py-1.5 rounded-full text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
            >Cancel</button>
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              data-testid="compose-send"
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${canSend ? "bg-cyan-600 text-white hover:bg-cyan-700" : "bg-slate-100 text-slate-400 cursor-not-allowed"}`}
            >
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              {sending ? "Sending…" : "Send email"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Phase 2L-A34 — Customer Details edit modal (Admin + Lab Tech)
// ──────────────────────────────────────────────────────────────────
function CustomerDetailsEditModal({ open, onClose, reference, customer, postedKitAddress, onSaved }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  // Structured delivery address fields — Starshipit rejects unparsed
  // single-line strings, so the admin edit modal must capture each
  // component separately. Seeded from an existing `posted_kit_address`
  // dict when present, otherwise best-effort parsed from the flat
  // `customer.address` string via `parseAusAddress`.
  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [suburb, setSuburb] = useState("");
  const [state, setState] = useState("");
  const [postcode, setPostcode] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Seed defaults whenever the modal opens (refresh after each save).
  useEffect(() => {
    if (open) {
      setName(customer?.name || "");
      setEmail(customer?.email || "");
      setMobile(customer?.mobile || "");
      const seed = (postedKitAddress && postedKitAddress.line1)
        ? postedKitAddress
        : parseAusAddress(customer?.address || "");
      setLine1(seed.line1 || "");
      setLine2(seed.line2 || "");
      setSuburb(seed.suburb || "");
      setState((seed.state || "").toUpperCase());
      setPostcode(seed.postcode || "");
      setReason("");
      setError(null);
    }
  }, [open, customer?.name, customer?.email, customer?.mobile, customer?.address,
      postedKitAddress?.line1, postedKitAddress?.line2, postedKitAddress?.suburb,
      postedKitAddress?.state, postedKitAddress?.postcode]);

  const validEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const emailValid = email.trim() === "" || validEmail(email.trim());
  const emailChanged = (email || "").trim() !== (customer?.email || "").trim();

  const AU_STATES = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"];
  const stateValid = state.trim() === "" || AU_STATES.includes(state.trim().toUpperCase());
  const postcodeValid = postcode.trim() === "" || /^\d{4}$/.test(postcode.trim());

  // Compose a canonical single-line address string so the flat
  // `customer.address` remains readable across the app (labels, COA,
  // lab queue) alongside the structured `posted_kit_address` we send.
  // Includes ", Australia" so this preview matches the backend's
  // recomposed value on reload (routers/bookings.py builds the same shape).
  const composedAddress = [
    line1.trim(),
    line2.trim(),
    `${suburb.trim()} ${state.trim().toUpperCase()} ${postcode.trim()}`.replace(/\s+/g, " ").trim(),
    "Australia",
  ].filter(Boolean).join(", ");

  const seedAddr = (postedKitAddress && postedKitAddress.line1)
    ? postedKitAddress
    : parseAusAddress(customer?.address || "");
  const addressChanged = (
    line1.trim() !== (seedAddr.line1 || "").trim() ||
    line2.trim() !== (seedAddr.line2 || "").trim() ||
    suburb.trim() !== (seedAddr.suburb || "").trim() ||
    state.trim().toUpperCase() !== (seedAddr.state || "").toUpperCase() ||
    postcode.trim() !== (seedAddr.postcode || "").trim()
  );

  const changed =
    name.trim() !== (customer?.name || "").trim() ||
    email.trim() !== (customer?.email || "").trim() ||
    mobile.trim() !== (customer?.mobile || "").trim() ||
    addressChanged;

  const addressComplete = line1.trim() && suburb.trim() && state.trim() && postcode.trim();
  // Only require a field to be filled if the user is actively changing
  // it — legacy bookings with a missing mobile/email must still be
  // editable for the address alone. `changed` guarantees at least one
  // real change is present before Save enables.
  const nameOk    = name.trim() !== (customer?.name || "").trim() ? !!name.trim() : true;
  const emailOk   = email.trim() !== (customer?.email || "").trim() ? !!email.trim() : true;
  const mobileOk  = mobile.trim() !== (customer?.mobile || "").trim() ? !!mobile.trim() : true;
  const addressOk = addressChanged ? addressComplete : true;
  const allFilled = nameOk && emailOk && mobileOk && addressOk;
  const canSave = !saving && changed && allFilled && emailValid && stateValid && postcodeValid;

  async function handleSave() {
    if (!canSave) return;
    setError(null);
    setSaving(true);
    try {
      // Only send fields that actually changed — server treats omitted
      // keys as "leave unchanged" and rejects blank values.
      const payload = {};
      if (name.trim() !== (customer?.name || "").trim()) payload.name = name.trim();
      if (email.trim() !== (customer?.email || "").trim()) payload.email = email.trim();
      if (mobile.trim() !== (customer?.mobile || "").trim()) payload.mobile = mobile.trim();
      if (addressChanged) {
        payload.posted_kit_address = {
          line1: line1.trim(),
          line2: line2.trim() || undefined,
          suburb: suburb.trim(),
          state: state.trim().toUpperCase(),
          postcode: postcode.trim(),
          country: "Australia",
        };
        // Also mirror to the flat string so any legacy consumer still
        // reads a sensible one-line address. The backend recomposes
        // this on its own, but sending it here keeps the local UI
        // state consistent if the server rejects for any reason.
        payload.address = composedAddress;
      }
      if (reason.trim()) payload.reason = reason.trim();

      await api.patch(`/admin/bookings/${reference}/customer-details`, payload);
      toast.success("Customer details updated");
      onSaved?.();
    } catch (e) {
      const msg = formatApiError(e?.response?.data?.detail) || e.message || "Failed to update";
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="customer-edit-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="w-4 h-4 text-cyan-700" />
            Edit customer details · <span className="font-mono text-sm text-slate-500">{reference}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Updates the booking&apos;s contact record. Already-sent emails are not changed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <Field label="Name">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={500}
              data-testid="customer-edit-name"
              className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 focus:border-cyan-500 focus:outline-none"
            />
          </Field>

          <Field label="Email">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={500}
              data-testid="customer-edit-email"
              className={`w-full text-sm px-3 py-2 rounded-lg border focus:outline-none ${emailValid ? "border-slate-200 focus:border-cyan-500" : "border-rose-300 focus:border-rose-500"}`}
            />
            {!emailValid && (
              <div className="text-[11px] text-rose-600 mt-1" data-testid="customer-edit-email-error">Enter a valid email address.</div>
            )}
            {emailChanged && emailValid && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mt-1.5 flex items-start gap-1.5" data-testid="customer-edit-email-warning">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                Future emails for this booking will be sent to the updated email address.
              </div>
            )}
          </Field>

          <Field label="Mobile">
            <input
              type="text"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              maxLength={500}
              data-testid="customer-edit-mobile"
              className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 focus:border-cyan-500 focus:outline-none"
            />
          </Field>

          <Field label="Address">
            <div className="space-y-2" data-testid="customer-edit-address-group">
              <div className="text-[11px] text-slate-500 -mt-0.5">
                Structured so shipping labels (Starshipit / AusPost) accept the destination.
              </div>
              <input
                type="text"
                value={line1}
                onChange={(e) => setLine1(e.target.value)}
                maxLength={200}
                placeholder="Street address (e.g. 12 Coastal Rd)"
                data-testid="customer-edit-address-line1"
                className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 focus:border-cyan-500 focus:outline-none"
              />
              <input
                type="text"
                value={line2}
                onChange={(e) => setLine2(e.target.value)}
                maxLength={200}
                placeholder="Line 2 (unit/apt — optional)"
                data-testid="customer-edit-address-line2"
                className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 focus:border-cyan-500 focus:outline-none"
              />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input
                  type="text"
                  value={suburb}
                  onChange={(e) => setSuburb(e.target.value)}
                  maxLength={120}
                  placeholder="Suburb"
                  data-testid="customer-edit-address-suburb"
                  className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 focus:border-cyan-500 focus:outline-none"
                />
                <select
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  data-testid="customer-edit-address-state"
                  className={`w-full text-sm px-3 py-2 rounded-lg border focus:outline-none bg-white ${stateValid ? "border-slate-200 focus:border-cyan-500" : "border-rose-300 focus:border-rose-500"}`}
                >
                  <option value="">State…</option>
                  {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input
                  type="text"
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value.replace(/[^\d]/g, "").slice(0, 4))}
                  maxLength={4}
                  inputMode="numeric"
                  placeholder="Postcode"
                  data-testid="customer-edit-address-postcode"
                  className={`w-full text-sm px-3 py-2 rounded-lg border focus:outline-none ${postcodeValid ? "border-slate-200 focus:border-cyan-500" : "border-rose-300 focus:border-rose-500"}`}
                />
              </div>
              {!postcodeValid && (
                <div className="text-[11px] text-rose-600" data-testid="customer-edit-address-postcode-error">Postcode must be 4 digits.</div>
              )}
              {composedAddress && (
                <div className="text-[11px] text-slate-500" data-testid="customer-edit-address-preview">
                  Preview: <span className="font-medium text-slate-700">{composedAddress}</span>
                </div>
              )}
            </div>
          </Field>

          <Field label="Reason (optional)">
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={300}
              placeholder="e.g. Customer reported typo in email"
              data-testid="customer-edit-reason"
              className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 focus:border-cyan-500 focus:outline-none"
            />
            <div className="text-[10px] text-slate-400 mt-1">
              Logged to the audit trail alongside the changes.
            </div>
          </Field>

          {error && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 p-2.5 text-xs text-rose-700" data-testid="customer-edit-error">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              data-testid="customer-edit-cancel"
              className="px-3 py-1.5 rounded-full text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
            >Cancel</button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              data-testid="customer-edit-save"
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${canSave ? "bg-cyan-600 text-white hover:bg-cyan-700" : "bg-slate-100 text-slate-400 cursor-not-allowed"}`}
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
